/**
 * WhyWorry player controller — Phase 3.
 *
 * Responsibilities (per kit 02-ARCHITECTURE "browser and server separation"):
 *  - finite state machine: idle → authorizing → resolving → loading →
 *    ready → playing/paused → ended (+ buffering + unavailable + error);
 *  - the kit handshake: announce ready to the parent, receive a one-time
 *    ticket via exact-origin postMessage; on localhost dev (no partner site
 *    exists yet) a dev-only self-ticket fallback kicks in;
 *  - session client: redeem → load → fail over through session sources →
 *    refresh tokens before expiry. Tokens live in memory ONLY;
 *  - Shaka Player mounts behind the approved controls.
 */
import shaka from "shaka-player";
import { icon, hydrate, setIcon } from "./icons.js";
import { startAds, type AdsPlan } from "./ads.js";

const $ = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

function escapeHtml(text: string): string {
  return String(text).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c] as string);
}

// ─── Content selector (mirrors the locked server contract) ───────────────────

type ContentSelector =
  | { type: "movie"; provider: "tmdb"; id: number }
  | { type: "tv"; provider: "tmdb"; id: number; season: number; episode: number }
  | { type: "anime"; provider: "anilist"; id: number; episode: number };

function parseEmbedPath(): ContentSelector | null {
  const m = location.pathname.match(/^\/embed\/(movie|tv|anime)\/(\d+)(?:\/(\d+))?(?:\/(\d+))?$/);
  if (!m) return null;
  const [, kind, idA, a, b] = m;
  const id = Number.parseInt(idA ?? "", 10);
  if (kind === "movie" && id > 0 && !a) {
    return { type: "movie", provider: "tmdb", id };
  }
  if (kind === "tv") {
    const season = Number.parseInt(a ?? "", 10);
    const episode = Number.parseInt(b ?? "", 10);
    if (season > 0 && episode > 0) {
      return { type: "tv", provider: "tmdb", id, season, episode };
    }
  }
  if (kind === "anime") {
    const episode = Number.parseInt(a ?? "", 10);
    const track = new URLSearchParams(location.search).get("track") === "dub" ? "dub" : "sub";
    if (episode > 0) return { type: "anime", provider: "anilist", id, episode, track } as ContentSelector;
  }
  return null;
}

function selectorFromQuery(): ContentSelector | null {
  const q = new URLSearchParams(location.search);
  const id = Number.parseInt(q.get("id") ?? "", 10);
  if (!Number.isInteger(id) || id <= 0) return null;
  const kind = q.get("type") ?? "movie";
  if (kind === "movie") return { type: "movie", provider: "tmdb", id };
  if (kind === "tv") {
    const season = Number.parseInt(q.get("season") ?? "1", 10);
    const episode = Number.parseInt(q.get("episode") ?? "1", 10);
    if (season > 0 && episode > 0) return { type: "tv", provider: "tmdb", id, season, episode };
  }
  if (kind === "anime") {
    // AniList id namespace (9ime). ?type=anime&id=21&episode=1100&track=sub
    const episode = Number.parseInt(q.get("episode") ?? "1", 10);
    const track = q.get("track") === "dub" ? "dub" : "sub";
    if (episode > 0) return { type: "anime", provider: "anilist", id, episode, track } as ContentSelector;
  }
  return null;
}

// ─── Session client (tokens in module memory only) ───────────────────────────

interface PublicPlayback {
  sourceId: string;
  manifestUrl: string;
  format: "hls" | "dash" | "mp4" | "other";
  mediaGrantExpiresAt: string;
  timelineVersion: string;
  /** `onDemand`: manual-click server (resolved on click, never auto-failover). */
  sources: { sourceId: string; label: string; onDemand?: true }[];
  /** Last-resort box fallback for segments (server EDGE_ORIGIN_FALLBACK=1). */
  originFallback?: boolean;
  /** Worker bases to retry a failed edge segment on (before origin). */
  edgeWorkers?: string[];
}

/** Now-playing metadata (mirrors the server's PublicTitleMeta). */
interface PublicMeta {
  name: string;
  logoUrl?: string;
  posterUrl?: string;
  year?: number;
  rating?: string;
  genres?: string[];
  overview?: string;
  runtime?: number;
  episodeName?: string;
  episodeLabel?: string;
}

interface PublicSession {
  sessionId: string;
  accessToken: string;
  refreshToken: string;
  title: string;
  posterUrl?: string;
  meta?: PublicMeta;
  playback: PublicPlayback;
  sessionExpiresAt: string;
}

const memory: {
  session: PublicSession | null;
  content: ContentSelector | null;
  generation: number;
  activeSourceIndex: number;
} = { session: null, content: null, generation: 0, activeSourceIndex: 0 };

function labelOf(state: string): string {
  return ({ movie: "MOVIE", tv: "TV SERIES", anime: "ANIME" } as Record<string, string>)[state] ?? state.toUpperCase();
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, init);
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON */
    }
    return { status: res.status, data: data as T };
  } catch {
    return { status: 0, data: null };
  }
}

/** Framed by the WhyWorry website playground (`?playground=1`). */
function playgroundParent(): string | null {
  if (new URLSearchParams(location.search).get("playground") !== "1") return null;
  try {
    const anc = (location as unknown as { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    if (anc && anc.length > 0) return anc[0]!;
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
}

async function playgroundTicket(content: ContentSelector, parentOrigin: string): Promise<string | null> {
  const { data } = await fetchJson<{ ticket?: string }>("/v1/playground/tickets", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ content, parentOrigin }),
  });
  return data?.ticket ?? null;
}

async function devSelfTicket(content: ContentSelector): Promise<string | null> {
  // DEV-ONLY fallback for localhost: no partner backend exists to mint a
  // ticket. The dev route lives only outside production server builds.
  const { data } = await fetchJson<{ ticket?: string }>("/v1/dev/tickets", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ content }),
  });
  return data?.ticket ?? null;
}

/** Last /v1/sessions error code (e.g. ORIGIN_NOT_ALLOWED for a site that is not allowed). */
let redeemError: string | null = null;
/** The partner page this player is framed on (set once by the start handshake). */
let embedOriginMemo: string | null = null;

async function redeem(ticket: string, embedOrigin: string | null = embedOriginMemo): Promise<PublicSession | null> {
  const { data } = await fetchJson<PublicSession | { error?: { code?: string } }>("/v1/sessions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // Origin header comes from the browser automatically; embedOrigin is the
    // partner page this player is framed on (browser-set postMessage origin).
    body: JSON.stringify({
      ticket,
      ...(embedOrigin ? { embedOrigin } : {}),
      // admin47 → Ads "test" mode: ads only for embeds opened with ?adtest=1.
      ...(new URLSearchParams(location.search).get("adtest") === "1" ? { adtest: true } : {}),
    }),
  });
  if (data && "playback" in (data as PublicSession)) return data as PublicSession;
  redeemError = (data as { error?: { code?: string } } | null)?.error?.code ?? null;
  return null;
}

// ─── The approved DOM surface ────────────────────────────────────────────────

type Phase =
  | "idle"
  | "authorizing"
  | "resolving"
  | "loading"
  | "ready"
  | "playing"
  | "paused"
  | "ended"
  | "buffering"
  | "unavailable"
  | "error";

const video = $("ww-video") as HTMLVideoElement;
const playerEl = $("player");
const timeline = $<HTMLInputElement>("timeline");
const playBtn = $("play-btn");
const centerPlay = $("center-play");
const playHint = $("play-hint");
const loadingEl = $("player-loading");
const loadingText = $("player-loading-text");
const filmTitle = $("film-title");
const filmEyebrow = $("film-eyebrow");
const filmMeta = $("film-meta-text");
const qualityBadge = $("quality-badge");
const serverValue = $("server-value");
const serverIndicator = $("server-indicator");
const currentTimeEl = $("current-time");
const durationEl = $("duration-time");
const chapterLabel = $("chapter-label");
const captionPreview = $("caption-preview");
const feedbackEl = $("gesture-feedback");
const menuEl = $("player-menu");
const toastEl = $("toast");
const subtitleValue = $("subtitle-value");
const speedBtn = $("#speed-btn") ?? $("speed-btn");

// ─── Now-watching overlay (pause card + start splash) ───────────────────────

const nowEl = $("now-watching");
const nwLogo = $<HTMLImageElement>("nw-logo");
const nwTitle = $("nw-title");
const nwEpisode = $("nw-episode");
const nwSe = $("nw-se");
const nwEpName = $("nw-ep-name");
const nwBadges = $("nw-badges");
const nwOverview = $("nw-overview");
const nwVerbIcon = $("nw-verb-icon");
const nwVerbText = $("nw-verb-text");
const nwProgressNow = $("nw-progress-now");
const nwRemaining = $("nw-remaining");

/** How long the splash lingers after (re)starting playback before it fades. */
const NW_SPLASH_MS = 2_000; // owner: gone after 2 s so the episode owns the screen
let nwTimer: ReturnType<typeof setTimeout> | undefined;

function nwShowing(): boolean {
  return nowEl.classList.contains("show");
}

function fallbackEpisodeLabel(c: ContentSelector | null): string {
  if (!c) return "";
  if (c.type === "tv") return `S${String(c.season).padStart(2, "0")} · E${String(c.episode).padStart(2, "0")}`;
  if (c.type === "anime") return `Episode ${String(c.episode).padStart(2, "0")}`;
  return "";
}

/** Fill the card once per session; absent meta fields simply hide their slot. */
function populateNowWatching(): void {
  const session = memory.session;
  if (!session) return;
  const m = session.meta;
  const name = m?.name ?? session.title ?? "Untitled";
  document.title = `${name} · WhyWorry`;

  if (m?.logoUrl) {
    nwLogo.setAttribute("src", m.logoUrl);
    nwLogo.setAttribute("alt", name);
    nwLogo.hidden = false;
    nwTitle.hidden = true;
  } else {
    nwLogo.hidden = true;
    nwTitle.hidden = false;
    nwTitle.textContent = name;
  }

  const label = m?.episodeLabel ?? fallbackEpisodeLabel(memory.content);
  const hasEpisode = !!label && memory.content?.type !== "movie";
  nwEpisode.hidden = !hasEpisode;
  if (hasEpisode) {
    nwSe.textContent = label;
    nwEpName.textContent = m?.episodeName ?? "";
    nwEpName.hidden = !m?.episodeName;
  }

  const parts: string[] = [];
  if (m?.rating) parts.push(`<span class="nw-cert">${escapeHtml(m.rating)}</span>`);
  if (m?.year) parts.push(`<span>${m.year}</span>`);
  if (m?.runtime) {
    parts.push(`<span>${Math.floor(m.runtime / 60)}h ${m.runtime % 60}m</span>`);
  }
  for (const genre of (m?.genres ?? []).slice(0, 3)) {
    parts.push(`<span>${escapeHtml(genre)}</span>`);
  }
  nwBadges.innerHTML = parts.join("");
  nwBadges.hidden = parts.length === 0;

  nwOverview.textContent = m?.overview ?? "";
  nwOverview.hidden = !m?.overview;
}

function updateNwVerb(): void {
  const playing = phase === "playing" || phase === "buffering";
  nwVerbIcon.innerHTML = playing ? icon("pause") : "❚❚";
  nwVerbText.textContent = playing ? "Now Playing" : "Paused";
}

/** Netflix pattern: padding = the card stays up; playing = it fades after a beat. */
function showNowWatching(splash: boolean): void {
  if (!memory.session) return;
  if (phase === "ended" || phase === "unavailable" || phase === "error") return;
  updateNwVerb();
  nowEl.classList.add("show");
  clearTimeout(nwTimer);
  nwTimer = undefined;
  if (splash) {
    nwTimer = setTimeout(() => nowEl.classList.remove("show"), NW_SPLASH_MS);
  }
}

function hideNowWatching(): void {
  clearTimeout(nwTimer);
  nowEl.classList.remove("show");
}

/** Remaining time + wall-clock end, refreshed with every timeupdate. */
function updateNwProgress(): void {
  if (!nwShowing() || !Number.isFinite(video.duration) || video.duration <= 0) return;
  const pct = Math.max(0, Math.min(100, (video.currentTime / video.duration) * 100));
  nwProgressNow.style.width = `${pct}%`;
  const remainingSec = Math.max(0, Math.ceil((video.duration - video.currentTime) / (video.playbackRate || 1)));
  const endsAt = new Date(Date.now() + remainingSec * 1000).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  nwRemaining.innerHTML = `<b>${formatTime(remainingSec)}</b>&thinsp;left&nbsp;&nbsp;•&nbsp;&nbsp;Ends ${escapeHtml(endsAt)}`;
}

let shakaPlayer: shaka.Player | null = null;

function formatTime(sec: number): string {
  const t = Math.max(0, Math.floor(sec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function toast(message: string): void {
  toastEl.querySelector("span")!.textContent = message;
  toastEl.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove("show"), 3200);
}

let feedbackTimer: ReturnType<typeof setTimeout> | undefined;
function feedback(message: string): void {
  feedbackEl.textContent = message;
  feedbackEl.classList.add("visible");
  clearTimeout(feedbackTimer);
  feedbackTimer = setTimeout(() => feedbackEl.classList.remove("visible"), 1000);
}

let phase: Phase = "idle";
/** Why playback hasn't started yet (reported with beats until the first frame). */
const preStart: { ap?: "blocked" | "off" | "ok"; rj?: string; err?: string; clicked: boolean } = { clicked: false };
function setPhase(next: Phase): void {
  phase = next;
  playerEl.classList.toggle("playing", next === "playing");
  playerEl.classList.toggle("buffering", next === "buffering");
  // First start: the full-screen branded loader owns the wait. After the
  // first frame, waits use the small brand mark with a friendly line.
  if (!firstFrameDone && (next === "ready" || next === "playing" || next === "paused")) finishLoader();
  if (!firstFrameDone && (next === "unavailable" || next === "error")) loaderEl.classList.add("done");
  const spinning: Phase[] = ["authorizing", "resolving", "loading", "buffering"];
  // The switch reveal owns the screen while it is up.
  loadingEl.hidden = !firstFrameDone || !spinning.includes(next) || switchEl.classList.contains("show");
  if (next === "authorizing" || next === "resolving") loadingText.textContent = "Lining up your episode…";
  if (next === "loading") loadingText.textContent = "Switching servers — hang tight…";
  if (next === "buffering") loadingText.textContent = "Catching the next scene…";
  setIcon(playBtn, next === "playing" ? "pause" : "play");
  playBtn.setAttribute("aria-label", next === "playing" ? "Pause" : "Play");
  centerPlay.setAttribute("aria-label", next === "playing" ? "Pause" : "Play");
  playHint.hidden = next !== "ready" && next !== "idle" && next !== "paused";
}

function showUnavailable(message: string): void {
  // Tell the partner so it can fall back to its own player (9ime native).
  if (window.parent !== window) {
    try {
      window.parent.postMessage({ type: "whyworry:unavailable", reason: message.slice(0, 120), content: memory.content }, "*");
    } catch {
      /* best effort */
    }
  }
  setPhase("unavailable");
  hideNowWatching();
  playHint.textContent = message;
  playHint.hidden = false;
  playHint.classList.add("play-hint-error");
  centerPlay.hidden = true;
  loadingEl.hidden = true;
}

filmTitle.textContent = "Resolving title…";
filmMeta.textContent = "Connecting to the WhyWorry engine…";
filmEyebrow.textContent = "WHYWORRY EMBED";

// ─── Shaka integration ───────────────────────────────────────────────────────

// ─── Image-wrapped MPEG-TS unwrapping (YAMI/Reanime scheme) ─────────────────

/**
 * YAMI's segments are image-stub-wrapped MPEG-TS (measured 2026-10-22: a
 * ~72-byte PNG stub, or a WEBP head, followed by raw TS; at least one
 * upstream flavor XOR-cycles the payload — see
 * STREAM_EMBED_RESEARCH_AND_PLAN.md). A browser deregisters the fake image
 * happily, but the MSE demuxer errors on it without stripping.
 *
 * The response filter below runs on EVERY segment shaka fetches — grant-
 * relayed or fetched direct from the CDN (Streams with directSegments
 * bypass the relay entirely, CORS-verified on the segment host). Plain
 * unstubbed segments from every other provider fail the magic check and
 * pass through byte-identical.
 */
const STUB_SCAN_BYTES = 2048;
const STUB_MIN_SYNC_STRIDES = 4;
const FLIX_IMAGE_XOR_KEY = new Uint8Array([
  157, 42, 241, 71, 179, 142, 92, 112, 166, 25, 228, 59, 216, 98, 15, 197,
]);

interface StubVerdict {
  offset: number;
  xor: boolean;
}

function stubImageMagic(b: Uint8Array): boolean {
  return (
    (b.length >= 8 &&
      b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 &&
      b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) ||
    (b.length >= 12 &&
      b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
      b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50)
  );
}

function stubStridesOk(b: Uint8Array, start: number, xor: boolean): boolean {
  let hits = 0;
  for (let i = 0; i < STUB_MIN_SYNC_STRIDES; i++) {
    const at = start + i * 188;
    if (at >= b.length) break;
    // XOR cycles from the stub end — offset-relative key indexing, matching
    // the published unwrap and the server relay.
    const v = xor
      ? b[at]! ^ FLIX_IMAGE_XOR_KEY[(at - start) % FLIX_IMAGE_XOR_KEY.length]!
      : b[at]!;
    if (v !== 0x47) return false;
    hits += 1;
  }
  return hits === STUB_MIN_SYNC_STRIDES;
}

function classifyStub(b: Uint8Array): StubVerdict | null {
  if (!stubImageMagic(b)) return null;
  const minStart = b[0] === 0x89 ? 8 : 12;
  const maxStart = Math.min(b.length, STUB_SCAN_BYTES) - 188 * STUB_MIN_SYNC_STRIDES;
  for (let start = minStart; start <= maxStart; start++) {
    if (stubStridesOk(b, start, false)) return { offset: start, xor: false };
  }
  for (let start = minStart; start <= maxStart; start++) {
    if (stubStridesOk(b, start, true)) return { offset: start, xor: true };
  }
  return null;
}

function stubStripResponseFilter(
  _type: shaka.net.NetworkingEngine.RequestType,
  data: shaka.extern.Response
): void {
  if (data.data == null) return;
  // shaka hands filters a plain ArrayBuffer (not a view). Treating it as a
  // view read ZERO bytes, so the stub was never stripped and every YAMI
  // segment reached MSE as image/png → 4032 (measured 2026-10-25).
  const src = data.data as ArrayBuffer | ArrayBufferView;
  // Independent copy: shaka may reuse the response buffer internally.
  const bytes =
    src instanceof ArrayBuffer
      ? new Uint8Array(src).slice()
      : new Uint8Array(src.buffer as ArrayBuffer, src.byteOffset, src.byteLength).slice();
  if (bytes.length < 188 * STUB_MIN_SYNC_STRIDES + 8) return;
  const verdict = classifyStub(bytes.subarray(0, STUB_SCAN_BYTES));
  if (!verdict) return;
  const payload = bytes.slice(verdict.offset);
  if (verdict.xor) {
    for (let i = 0; i < payload.length; i++) {
      payload[i] = payload[i]! ^ FLIX_IMAGE_XOR_KEY[i % FLIX_IMAGE_XOR_KEY.length]!;
    }
  }
  data.data = payload;
}

// ─── Segment MIME normalisation ─────────────────────────────────────────────
//
// Extensionless HLS segments make shaka guess the container from the
// response Content-Type (hls_parser guessMimeType_). Upstreams disguise TS:
// measured 2026-10-24 — megavid `image/jpeg`, AniGato `image/png` /
// `text/html`, nexabloom via relay `text/plain`. shaka then builds a
// SourceBuffer for "image/jpeg", the video track never initialises and only
// audio plays (the owner's "poster + audio only" report). Correct the label
// whenever the bytes are MPEG-TS (or for a body-less HEAD probe answered with
// a disguise type). Genuine JSON/HTML error bodies are left untouched.
const DISGUISE_TYPE = /^(image\/|text\/|application\/octet-stream|binary\/)/i;

function looksTs(b: Uint8Array): boolean {
  return b.length >= 189 && b[0] === 0x47 && b[188] === 0x47;
}

function segmentMimeResponseFilter(
  type: shaka.net.NetworkingEngine.RequestType,
  response: shaka.extern.Response
): void {
  if (type !== shaka.net.NetworkingEngine.RequestType.SEGMENT) return;
  const ct = response.headers["content-type"] ?? "";
  if (!DISGUISE_TYPE.test(ct)) return;
  const view = response.data as ArrayBufferView | ArrayBuffer | null;
  const bytes =
    view == null
      ? new Uint8Array(0)
      : view instanceof ArrayBuffer
        ? new Uint8Array(view, 0, Math.min(view.byteLength, 400))
        : new Uint8Array(view.buffer as ArrayBuffer, view.byteOffset, Math.min(view.byteLength, 400));
  if (looksTs(bytes)) response.headers["content-type"] = "video/mp2t";
  else if (looksFmp4(bytes)) response.headers["content-type"] = "video/mp4";
  if (import.meta.env.DEV) {
    console.log(`[whyworry] mime ${ct} -> ${response.headers["content-type"]} bytes=${bytes.length} head=${Array.from(bytes.subarray(0, 4)).map((x) => x.toString(16)).join("")} ${(response.uri ?? "").slice(8, 40)}`);
  }
}

function looksFmp4(b: Uint8Array): boolean {
  if (b.length < 8) return false;
  const box = String.fromCharCode(b[4]!, b[5]!, b[6]!, b[7]!);
  return box === "ftyp" || box === "styp" || box === "moof" || box === "sidx";
}

// shaka's MIME probe for extensionless segments is a HEAD request. Relays
// answer HEAD with 405 and some CDNs with no Content-Type, and then shaka
// silently assumes MP4 for a TS stream (measured: Nexa's first variant
// mounted as video/mp4 → 0 frames decoded). Turn the probe into a tiny ranged
// GET so the bytes themselves decide (segmentMimeResponseFilter).
function mimeProbeRequestFilter(
  type: shaka.net.NetworkingEngine.RequestType,
  request: shaka.extern.Request
): void {
  if (type !== shaka.net.NetworkingEngine.RequestType.SEGMENT || request.method !== "HEAD") return;
  request.method = "GET";
  // 4 KiB: enough for an image stub + 4 TS sync strides to be recognised.
  // Only on OUR doors (origin grants, our Workers): a Range header on a
  // third-party URL that redirects cross-origin (AniGato Server 6:
  // anigato.lol/ts → random *.workers.dev) is aborted, the probe fails and
  // shaka falls back to "video/mp4" for a TS stream → nothing buffers
  // (measured 2026-10-25). Third-party segments get a plain GET once.
  const u = request.uris[0] ?? "";
  const ours = u.startsWith("/") || u.startsWith(location.origin) || /\/proxy\?u=/.test(u);
  if (ours) request.headers["Range"] = "bytes=0-4095";
  if (import.meta.env.DEV) console.log(`[whyworry] mime probe -> GET ${(request.uris[0] ?? "").slice(8, 40)}`);
}

// ─── Edge segments: Worker first, origin (Lightsail) only on failure ───────
//
// The relay hands segments to the browser as signed Worker URLs carrying the
// origin grant path in `wwf=`. Shaka walks `request.uris` in order on retry,
// so [worker, origin] = "Cloudflare carries the video; origin only when a
// Worker refuses". After EDGE_MAX_FALLBACKS fallbacks in one page, edge is
// skipped for the rest of the session (origin first) to avoid paying a
// failed Worker round-trip on every segment.
// 2026-10-26 (origin-bandwidth fix): a failed Worker segment now retries the
// OTHER Workers first ([primary, alt workers…, origin]); origin is only the
// last resort for that one segment. After EDGE_MAX_FALLBACKS failures the
// failing Worker moves to the back of the order — the episode never flips to
// "origin-first" any more (that flip carried 9 GB/day through the box).
const EDGE_MAX_FALLBACKS = 3;
let edgeFallbacks = 0;
/** Worker bases from the session (playback.edgeWorkers), demoted on repeated failure. */
let edgeOrder: string[] = [];
const edgeFails = new Map<string, number>();

function workerBaseOf(uri: string): string | null {
  const i = uri.indexOf("/proxy?");
  return i > 0 ? uri.slice(0, i) : null;
}

/**
 * Workers that just REFUSED a request (quota 429, 403, 5xx, Cloudflare 1027
 * "limit exceeded", or a timeout) are skipped for REFUSED_TTL_MS: new requests
 * try them only after every other Worker. Keyed by Worker base.
 */
const REFUSED_TTL_MS = 10 * 60_000;
const refusedUntil = new Map<string, number>();
const isRefused = (b: string) => (refusedUntil.get(b) ?? 0) > Date.now();
/** Worker → error tally since the last beat (e.g. "429": 3, "t": 1), reported to the server. */
let edgeErrTally: Record<string, number> = {};
/** Worker errors by Worker index, sent with the beat and reset. */
function takeEdgeErrors(): Record<string, number> | undefined {
  const t = edgeErrTally;
  edgeErrTally = {};
  return Object.keys(t).length ? t : undefined;
}

/**
 * Order of Workers to try for one request: the minted one first (that is how
 * the server spreads load), then the others in ring order starting after it,
 * with refused/demoted Workers moved to the end.
 */
function workerRing(minted: string): string[] {
  const bases = edgeOrder.length ? [...edgeOrder] : [minted];
  if (!bases.includes(minted)) bases.unshift(minted);
  // Ring order from the server's list, starting at the minted Worker.
  const server = memory.session?.playback.edgeWorkers?.length ? [...memory.session.playback.edgeWorkers] : bases;
  if (!server.includes(minted)) server.unshift(minted);
  const i = server.indexOf(minted);
  const ring = [...server.slice(i), ...server.slice(0, i)].filter((b) => bases.includes(b));
  const good = ring.filter((b) => !isRefused(b) && !edgeDemoted.has(b));
  const bad = ring.filter((b) => !good.includes(b));
  return [...good, ...bad];
}
const edgeDemoted = new Set<string>();

/** Shaka "retry" event: a request failed on one URI and moves to the next. */
function onNetRetry(ev: Event): void {
  const err = (ev as unknown as { error?: { code?: number; data?: unknown[] } }).error;
  const uri = String(err?.data?.[0] ?? "");
  const base = workerBaseOf(uri);
  if (!base) return;
  const code = err?.code;
  const status = code === 1001 ? Number(err?.data?.[1] ?? 0) : 0;
  // 1001 = bad HTTP status, 1002 = network/CORS error, 1003 = timeout.
  const key = code === 1001 ? String(status || "http") : code === 1003 ? "t" : code === 1002 ? "net" : `c${code ?? 0}`;
  const idx = (memory.session?.playback.edgeWorkers ?? []).indexOf(base);
  const tag = `w${idx >= 0 ? idx + 1 : "?"}:${key}`;
  edgeErrTally[tag] = (edgeErrTally[tag] ?? 0) + 1;
  // A refusal says the Worker itself can't serve right now (not one bad segment).
  if (status === 429 || status === 403 || status >= 500 || code === 1003 || code === 1002) {
    refusedUntil.set(base, Date.now() + REFUSED_TTL_MS);
  }
}

function syncEdgeOrder(): void {
  const fresh = memory.session?.playback.edgeWorkers ?? [];
  const kept = edgeOrder.filter((b) => fresh.includes(b));
  edgeOrder = [...kept, ...fresh.filter((b) => !kept.includes(b))];
}

function originOf(uri: string): string | null {
  const m = /[?&]wwf=([^&]+)/.exec(uri);
  if (!m) return null;
  try {
    return new URL(decodeURIComponent(m[1]!), location.href).toString();
  } catch {
    return null;
  }
}

function edgeFallbackRequestFilter(
  type: shaka.net.NetworkingEngine.RequestType,
  request: shaka.extern.Request
): void {
  if (type !== shaka.net.NetworkingEngine.RequestType.SEGMENT) return;
  const out: string[] = [];
  for (const u of request.uris) {
    const origin = originOf(u);
    const base = workerBaseOf(u);
    if (!origin || !base) {
      out.push(u);
      continue;
    }
    // Same signed path on every Worker (the signature is scoped to the
    // upstream host, not the Worker). Origin last.
    // 2026-11-03 (even load): the server spreads sessions across Workers by
    // writing a DIFFERENT Worker into each session's segment URLs. This filter
    // used to replace that with edgeOrder[0] whenever the minted Worker was in
    // the list — so nearly all traffic hit the FIRST Worker until its account
    // reached the soft cap, then the second, and so on (Cloudflare hourly
    // counts, Nov 2–3). Now the minted Worker goes first; retries continue
    // round the ring after it; demoted or refused Workers go last.
    const path = u.slice(base.length);
    for (const b of workerRing(base)) out.push(b + path);
    // 2026-10-29: origin only when the server allows it (EDGE_ORIGIN_FALLBACK=1). The Workers were
    // NOT failing (Cloudflare: 0 errors, ~1,100 clientDisconnected) — slow viewers hit the stall
    // timeout and fell through to the box (~1.2 GB/day). Now they retry the Workers instead.
    if (memory.session?.playback.originFallback) out.push(origin);
  }
  request.uris = [...new Set(out)]; // idempotent if the filter runs again on retry
}

function edgeFallbackResponseFilter(
  type: shaka.net.NetworkingEngine.RequestType,
  response: shaka.extern.Response
): void {
  if (type !== shaka.net.NetworkingEngine.RequestType.SEGMENT) return;
  const first = workerBaseOf(response.originalUri ?? "");
  const servedBy = workerBaseOf(response.uri ?? "");
  if (!first || !originOf(response.originalUri ?? "")) return;
  if (servedBy === first) return; // primary Worker served it
  // The primary Worker failed for this segment (another Worker or origin served it).
  edgeFallbacks += 1;
  const n = (edgeFails.get(first) ?? 0) + 1;
  edgeFails.set(first, n);
  if (n >= EDGE_MAX_FALLBACKS && edgeOrder.length > 1) {
    edgeOrder = [...edgeOrder.filter((b) => b !== first), first]; // demote, never "origin-first"
    edgeFails.set(first, 0);
    edgeDemoted.add(first);
  }
}

async function mountPlayback(manifestUrl: string, format: PublicPlayback["format"]): Promise<void> {
  setPhase("loading");
  playerEl.classList.remove("has-frame");
  // Per-source state: one bad source must not push the next one to origin-first.
  edgeFallbacks = 0;
  edgeDemoted.clear();
  syncEdgeOrder();
  console.log("[whyworry] mount begin fmt=%s url=%s", format, manifestUrl.slice(0, 60));
  if (format === "mp4") {
    // Direct progressive file — plain video element, no abr stack needed.
    // The server 302s to a signed Worker; if that fails, retry ONCE on the
    // origin relay (`edge=0`) before the source counts as failed.
    await unloadPlayer();
    await new Promise<void>((resolve, reject) => {
      let triedOrigin = false;
      let srcKind = "g"; // w = Worker URL (resolve=1), g = grant URL (server 302)
      let srcSetAt = performance.now();
      const cleanup = () => {
        video.removeEventListener("loadedmetadata", ok);
        video.removeEventListener("error", bad);
      };
      const ok = () => {
        cleanup();
        resolve();
      };
      // 2026-10-31: a failed Worker MP4 tries EVERY other Worker (same signed
      // URL, the token is scoped to the upstream host, not the Worker) before
      // the origin box. Mirrors the HLS segment retry (edgeOrder).
      const triedWorkers = new Set<string>();
      let workerHops = 0;
      const baseOf = (u: string) => { try { return new URL(u).origin; } catch { return ""; } };
      const nextWorkerUrl = (): string | null => {
        const cur = video.currentSrc || video.src;
        const curBase = baseOf(cur);
        if (!curBase || !/\.workers\.dev$/.test(new URL(curBase).hostname)) return null;
        triedWorkers.add(curBase);
        // A Worker that failed this file is skipped by the other MP4 plays of this tab too.
        refusedUntil.set(curBase, Date.now() + REFUSED_TTL_MS);
        const tag = `mp4:e${video.error?.code ?? 0}`;
        edgeErrTally[tag] = (edgeErrTally[tag] ?? 0) + 1;
        // Ring order after the current Worker (even spread), refused ones last.
        const all = (memory.session?.playback.edgeWorkers ?? []).map((b) => b.replace(/\/+$/, ""));
        const i = all.indexOf(curBase);
        const ring = i >= 0 ? [...all.slice(i + 1), ...all.slice(0, i)] : all;
        const fresh = ring.filter((b) => !triedWorkers.has(b));
        const next = fresh.find((b) => !isRefused(b)) ?? fresh[0];
        return next ? next + cur.slice(curBase.length) : null;
      };
      const bad = () => {
        if (srcKind === "w" && !triedOrigin) {
          const alt = nextWorkerUrl();
          if (alt) {
            workerHops++;
            console.warn(`[whyworry] mp4 worker failed (e${video.error?.code ?? 0}), trying worker ${workerHops + 1}: ${baseOf(alt)}`);
            srcSetAt = performance.now();
            video.src = alt;
            return;
          }
        }
        if (!triedOrigin) {
          triedOrigin = true;
          // 2026-10-31: tell the server WHY (browser MediaError code, which
          // URL failed, how long it took) — it is recorded as the origin reason.
          const code = video.error?.code ?? 0; // 1 aborted · 2 network · 3 decode · 4 src not supported
          const secs = (performance.now() - srcSetAt) / 1000;
          const t = secs < 5 ? "t0-5" : secs < 15 ? "t5-15" : secs < 30 ? "t15-30" : "t30+";
          // srcKind "w" + workerHops: e.g. e4-w3-t15-30 = all 3 Workers failed.
          const why = `e${code}-${srcKind}${srcKind === "w" ? workerHops + 1 : ""}-${t}`;
          console.warn(`[whyworry] mp4 edge failed (${why}: ${video.error?.message ?? ""}), retrying via origin`);
          video.src = manifestUrl + (manifestUrl.includes("?") ? "&" : "?") + "edge=0&why=" + why;
          return;
        }
        cleanup();
        reject(new Error("mp4 failed on edge and origin"));
      };
      video.addEventListener("loadedmetadata", ok);
      video.addEventListener("error", bad);
      // Ask for the Worker URL once and play it directly (long-lived token);
      // the grant URL stays the fallback when no Worker is available.
      void fetchJson<{ url?: string | null }>(manifestUrl + (manifestUrl.includes("?") ? "&" : "?") + "resolve=1")
        .then(({ status, data }) => {
          srcKind = status === 200 && data?.url ? "w" : "g";
          srcSetAt = performance.now();
          video.src = status === 200 && data?.url ? data.url : manifestUrl;
        })
        .catch(() => {
          srcKind = "g";
          srcSetAt = performance.now();
          video.src = manifestUrl;
        });
    });
    return;
  }
  if (!shakaPlayer) {
    shakaPlayer = new shaka.Player();
    console.log("[whyworry] shaka player created");
    try {
      await shakaPlayer.attach(video);
      console.log("[whyworry] shaka attach done");
    } catch (e) {
      console.error("[whyworry] shaka attach FAILED", e);
      throw e;
    }
    shakaPlayer.addEventListener("error", (e) => onPlayerError(e));
    // Every segment passes through here once; only stubbed ones are touched.
    shakaPlayer.getNetworkingEngine()?.registerResponseFilter(stubStripResponseFilter);
    // After the stub strip, so un-stubbed TS is what gets sniffed.
    shakaPlayer.getNetworkingEngine()?.registerResponseFilter(segmentMimeResponseFilter);
    shakaPlayer.getNetworkingEngine()?.registerRequestFilter(mimeProbeRequestFilter);
    shakaPlayer.getNetworkingEngine()?.registerRequestFilter(edgeFallbackRequestFilter);
    shakaPlayer.getNetworkingEngine()?.registerResponseFilter(edgeFallbackResponseFilter);
    // 2026-11-03: a Worker that refuses (quota/403/5xx/timeout) is skipped for 10 min.
    shakaPlayer.getNetworkingEngine()?.addEventListener("retry", onNetRetry);
    // Two attempts minimum so the [worker, origin] uri pair is always walked.
    // 2026-10-27 (loop/switch bug): 3 attempts × 300 ms gave up after ~1 s of
    // network trouble → CRITICAL 1002 → auto-switch → episode restart. Now:
    // patient backoff (~1+2+4+8+8 s), a 30 s buffer to ride out blips.
    const patient = { maxAttempts: 6, baseDelay: 1000, backoffFactor: 2, fuzzFactor: 0.3, timeout: 30_000, stallTimeout: 15_000, connectionTimeout: 10_000 };
    shakaPlayer.configure({
      streaming: {
        retryParameters: patient,
        // Start/resume with ~4 s buffered (2 short segments). 1 s started on the first
        // segment alone and froze at 0:03 while the second one loaded (owner report).
        rebufferingGoal: 2, // 4 s stalled ZORO at 3.7 s buffered; prefetch fixes the 0:03 freeze
        bufferingGoal: 30,
        bufferBehind: 30,
        // 2026-10-27 (stuck at 0:03): segments were fetched ONE at a time, so each
        // short TikTok/Worker segment paid a full round trip before the next began
        // and the buffer drained right after the first segment. Prefetch keeps
        // several segments in flight in parallel.
        segmentPrefetchLimit: 4,
      },
      manifest: { retryParameters: patient },
      // Start on a modest rendition (a 1080p first segment is 2–5 MB and was
      // most of the time-to-first-frame); ABR climbs within a few seconds.
      abr: { defaultBandwidthEstimate: 1_500_000 },
    });
  }
  // A manual quality pick belongs to the previous server only.
  shakaPlayer.configure({ abr: { enabled: true } });
  try {
    console.log("[whyworry] shaka load start");
    await shakaPlayer.load(manifestUrl);
    console.log("[whyworry] shaka load OK");
  } catch (e) {
    const se = e as { code?: number; data?: unknown };
    console.error(`[whyworry] shaka load FAILED code=${se?.code} data=${JSON.stringify(se?.data ?? null)?.slice(0, 400)}`);
    throw e;
  }
}

async function unloadPlayer(): Promise<void> {
  // AWAIT the destroy: it detaches from <video> and clears its src when it
  // finishes. Not awaiting it wiped the MP4 src we set right after, so a
  // switch to LEVI hung on "Preparing your stream" (2026-10-25).
  if (shakaPlayer) {
    const p = shakaPlayer;
    shakaPlayer = null;
    await p.destroy().catch(() => {});
  }
  video.removeAttribute("src");
  video.load();
}

// ─── Failover: session-scoped only, server-apprised ─────────────────────────

let failoverCycles = 0;

async function loadActiveSource(): Promise<void> {
  const session = memory.session;
  if (!session) return;
  const playback = session.playback;

  // Guard against stale generations overwriting a new title (kit rule).
  const generation = memory.generation;

  // The mount decides this source's fate: errors fired while it loads must
  // not start a parallel failover (that interrupted the load → 7000 → next
  // server → "Buffering…" forever on a manual switch).
  mounting = true;
  const seq = ++mountSeq;
  noteIntent(); // this source gets its own 8 s to show progress
  sourceProgressed = false;
  let failed = false;
  try {
    timeline.max = "100";
    qualityBadge.innerHTML = escapeHtml(serverLabel(playback));
    refreshServerChrome();
    resetSubtitleUi();
    await mountPlayback(playback.manifestUrl, playback.format);
    if (generation === memory.generation) {
      setPhase("ready");
      applyDefaultSubtitles();
    }
  } catch (e) {
    // Our own newer load superseded this one — the newer mount owns the outcome.
    if (isInterrupted(e)) return;
    failed = true;
  } finally {
    mounting = false;
  }
  // Only the LATEST mount may fail over (a watchdog switch may have replaced it).
  if (failed && generation === memory.generation && seq === mountSeq) await failover("source refused playback");
}

// ─── 8-second progress rule (owner, 2026-11-04) ─────────────────────────────
// While the viewer wants to watch — the player is loading a source, or the
// video is unpaused — the picture must move within STALL_SWITCH_MS. If it does
// not (first frame never came, or playback froze mid-episode), switch to the
// next server at once and tell the server, which lists that source last for
// this episode for the next viewers. Never while paused, never on the
// "press play" screen (autoplay blocked), never while the viewer is offline.
const STALL_SWITCH_MS = 5_000; // owner 2026-11-04: 5 s, not 8
let mountSeq = 0;
let stallClockAt = performance.now();
let lastProgressTime = -1;
/** The current source has shown at least one advancing frame. */
let sourceProgressed = false;
/** Last time the picture really moved (or the viewer acted). Data arriving does NOT move this. */
let movedAt = performance.now();
/** Hard cap: even while data trickles in, no movement for this long = switch. */
const STALL_HARD_MS = 10_000;
function noteIntent(): void {
  stallClockAt = performance.now();
  movedAt = stallClockAt;
  lastProgressTime = video.currentTime;
}
video.addEventListener("timeupdate", () => {
  if (video.currentTime !== lastProgressTime && !video.paused) {
    lastProgressTime = video.currentTime;
    stallClockAt = performance.now();
    movedAt = stallClockAt;
    sourceProgressed = true;
  }
});
video.addEventListener("play", noteIntent);
video.addEventListener("seeking", noteIntent);
// The server IS responding while media data keeps arriving (slow MP4 hosts
// such as GOJO need 5–7 s for the first frame): only a SILENT source — no new
// data and no movement for 8 s — gets switched.
let lastBufferedEnd = 0;
video.addEventListener("progress", () => {
  const b = video.buffered;
  const end = b.length ? b.end(b.length - 1) : 0;
  if (end > lastBufferedEnd + 0.05) {
    lastBufferedEnd = end;
    stallClockAt = performance.now();
  }
});
video.addEventListener("emptied", () => { lastBufferedEnd = 0; });
window.addEventListener("online", noteIntent);

function wantsToPlay(): boolean {
  if (!memory.session || video.ended) return false;
  if (phase === "paused" || phase === "ended" || phase === "unavailable" || phase === "error") return false;
  // "ready" + paused = the press-play screen (autoplay off/blocked): wait for the viewer.
  if (phase === "ready" && video.paused && !mounting) return false;
  return !video.paused || phase === "loading" || mounting;
}
document.addEventListener("visibilitychange", noteIntent);

setInterval(() => {
  if (failingOver || !wantsToPlay()) {
    if (!failingOver && (video.paused || !memory.session)) noteIntent();
    return;
  }
  // Viewer offline, or a background tab (browsers throttle timers there): not the server's fault.
  if (!navigator.onLine || document.hidden) return noteIntent();
  const nowMs = performance.now();
  if (nowMs - stallClockAt < STALL_SWITCH_MS && nowMs - movedAt < STALL_HARD_MS) return;
  // Safety: once EVERY server was tried without progress, keep loading the
  // current one (patient) instead of cycling into "unavailable".
  const pb = memory.session?.playback;
  if (pb) {
    if (triedGen !== memory.generation) { triedNoProgress.clear(); triedGen = memory.generation; }
    triedNoProgress.add(pb.sourceId);
    const cands = pb.sources.filter((s) => !s.onDemand).map((s) => s.sourceId);
    if (cands.length > 0 && cands.every((id) => triedNoProgress.has(id))) return;
  }
  const where = sourceProgressed ? "froze mid-episode" : "no first frame";
  console.warn(`[whyworry] ${where}: no progress for ${Math.round((nowMs - Math.max(stallClockAt, movedAt - STALL_HARD_MS + STALL_SWITCH_MS)) / 1000) || STALL_SWITCH_MS / 1000}s on ${memory.session ? serverLabel(memory.session.playback) : "?"} → switching`);
  stallSwitches += 1;
  const tag = sourceProgressed ? "stall:mid" : "stall:start";
  edgeErrTally[tag] = (edgeErrTally[tag] ?? 0) + 1;
  noteIntent();
  void failover(`8s rule: ${where}`, true);
}, 500);
let stallSwitches = 0;
/** The viewer's last real position in this episode (survives failed switches). */
let viewerPos = 0;
let viewerPosGen = -1;
const notePos = () => {
  if (video.currentTime > 0.5 && Number.isFinite(video.currentTime)) { viewerPos = video.currentTime; viewerPosGen = memory.generation; }
};
video.addEventListener("timeupdate", () => { if (sourceProgressed) notePos(); });
video.addEventListener("seeking", notePos);
/** Sources tried without any progress since the last time video actually moved (per episode). */
const triedNoProgress = new Set<string>();
let triedGen = -1;
video.addEventListener("timeupdate", () => { if (sourceProgressed && !video.paused && triedNoProgress.size) triedNoProgress.clear(); });
/** Consecutive 8 s-rule switches away from the same server family; reset once a source plays. */
let stallStreak = 0;
let stallFamily = "";
video.addEventListener("playing", () => { if (sourceProgressed) { stallStreak = 0; stallFamily = ""; } });
video.addEventListener("timeupdate", () => { if (sourceProgressed && stallStreak && !video.paused) { stallStreak = 0; stallFamily = ""; } });
// Read-only debug view for support/tests (no controls).
(window as unknown as { __wwStall?: () => unknown }).__wwStall = () => ({
  phase, mounting, failingOver, paused: video.paused, readyState: video.readyState,
  ageMs: Math.round(performance.now() - stallClockAt), progressed: sourceProgressed, switches: stallSwitches, wants: wantsToPlay(),
});

/**
 * Viewer-facing server names: the server only ("EREN"), never the CDN
 * ("· fetch.nexabloom.top"). Repeats are numbered: EREN, EREN 2, …
 */
function serverNames(sources: PublicPlayback["sources"]): Map<string, string> {
  const base = sources.map((s) => (s.label.split(" · ")[0] ?? s.label).trim() || "Server");
  const total = new Map<string, number>();
  for (const b of base) total.set(b, (total.get(b) ?? 0) + 1);
  const seen = new Map<string, number>();
  const out = new Map<string, string>();
  sources.forEach((s, i) => {
    const b = base[i]!;
    const n = (seen.get(b) ?? 0) + 1;
    seen.set(b, n);
    out.set(s.sourceId, total.get(b)! > 1 && n > 1 ? `${b} ${n}` : b);
  });
  return out;
}

function serverLabel(playback: PublicPlayback): string {
  return serverNames(playback.sources).get(playback.sourceId) ?? "Auto";
}

function refreshServerChrome(): void {
  const pb = memory.session?.playback;
  if (!pb) return;
  const name = serverLabel(pb);
  serverValue.innerHTML = `${escapeHtml(name)} <span class="dot green"></span>`;
  $("ww-server-name").textContent = name;
}

// shaka LOAD_INTERRUPTED: our own newer load superseded this one — not a fault.
const SHAKA_LOAD_INTERRUPTED = 7000;
let failingOver = false;
let mounting = false;

function isInterrupted(e: unknown): boolean {
  return (e as { code?: number } | null)?.code === SHAKA_LOAD_INTERRUPTED;
}

async function failover(reason: string, stalled = false): Promise<void> {
  // One failover at a time, and never while a mount is already deciding the
  // outcome (measured 2026-10-24: overlapping failovers interrupted each
  // other's loads — 7000 → "failure" → next source — and walked every server
  // until "unavailable"; the owner's "stuck on buffering after switching").
  if (failingOver) return;
  failingOver = true;
  try {
    await failoverInner(reason, stalled);
  } finally {
    failingOver = false;
    noteIntent(); // the next source gets a fresh 8 s
  }
}

async function failoverInner(reason: string, stalled = false): Promise<void> {
  const session = memory.session;
  if (!session) return;
  failoverCycles += 1;

  // Manual-click servers (Nexa Tokyo, PAPA) are never an automatic target.
  const pick = () => {
    const sources = session.playback.sources.filter((s) => !s.onDemand || s.sourceId === session.playback.sourceId);
    const currentIndex = sources.findIndex((s) => s.sourceId === session.playback.sourceId);
    return { sources, next: sources[(currentIndex + 1) % sources.length] };
  };
  let { sources, next } = pick();
  // 8 s rule, 2nd stall in a row on the same server family (e.g. YAMI 3 → YAMI 7
  // both hung): jump to a DIFFERENT server instead of paying 8 s per sibling.
  const family = (label: string) => (label.split(" · ")[0] ?? label).trim();
  const curFamily = family(sources.find((s) => s.sourceId === session.playback.sourceId)?.label ?? "");
  if (stalled) {
    stallStreak = stallFamily === curFamily ? stallStreak + 1 : 1;
    stallFamily = curFamily;
  }
  const preferOther = () => {
    // 5 s without progress: next = a YAMI that already proved its video loads in
    // this browser (start race, free), else a different server family, else any untried.
    if (!stalled) return;
    const i = sources.findIndex((s) => s.sourceId === session.playback.sourceId);
    const ring = [...sources.slice(i + 1), ...sources.slice(0, Math.max(0, i))].filter((s) => !s.onDemand && !triedNoProgress.has(s.sourceId));
    const pick = ring.find((s) => raceOk.has(s.sourceId)) ?? ring.find((s) => family(s.label) !== curFamily) ?? ring[0];
    if (pick) next = pick;
  };
  preferOther();
  // 2026-11-04: the preferred server (YAMI) often starts ALONE — the other
  // servers join the session a few seconds later, and the player only learned
  // about them after the first source finished loading. A source that hangs
  // never finishes, so the 8 s switch saw "no other server" → unavailable.
  // Ask the server for the current list first (up to ~4.5 s for late servers).
  for (let i = 0; i < 4 && (!next || next.onDemand || next.sourceId === session.playback.sourceId); i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, 1500));
    if (await refreshSourceList()) {
      ({ sources, next } = pick());
      preferOther();
    }
  }

  if (!next || next.onDemand || next.sourceId === session.playback.sourceId || failoverCycles > sources.length) {
    showUnavailable("This title isn't available right now. Try again shortly.");
    return;
  }

  const { status: st, data } = await fetchJson<{ playback?: PublicPlayback }>(
    `/v1/sessions/${session.sessionId}/source`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sourceId: next.sourceId, accessToken: session.accessToken, ...(stalled ? { stalled: true } : {}) }),
    }
  );
  if (st === 401 || st === 400) {
    // Session gone (API restart / expiry): silently start a fresh one, same spot.
    if (!(await recoverSession())) showUnavailable("Your session expired. Reload to watch.");
    return;
  }
  if (st !== 200 || !data?.playback) {
    showUnavailable("Playback is unavailable on every server right now.");
    return;
  }
  // Keep the viewer's spot: an automatic switch used to restart at 0:00.
  // A source that never loaded reports currentTime 0, so fall back to the
  // last position the viewer actually had (or seeked to) in this episode.
  const known = viewerPosGen === memory.generation ? viewerPos : 0;
  const resumeAt = video.currentTime > 3 ? video.currentTime : known > 3 ? known : 0;
  session.playback = data.playback;
  void reason;
  console.warn(`[whyworry] auto-switch (${reason}) → ${serverLabel(session.playback)} at ${resumeAt.toFixed(1)}s`);
  if (firstFrameDone) showSwitch(serverLabel(session.playback), "AUTO-SWITCHING TO");
  // A stalled source leaves hung requests inside the shaka instance, and the
  // next load on the SAME instance waited on them forever (measured locally:
  // every later server "load start", never "load OK"). Start the next server
  // on a fresh engine; don't wait more than 1.5 s for the old one to close.
  if (stalled) await Promise.race([unloadPlayer(), new Promise((r) => setTimeout(r, 1500))]);
  // Do NOT hold the failover lock while the new source loads: if it hangs
  // too, the 8 s rule must be free to move on to the next server.
  const gen = memory.generation;
  const target = session.playback.sourceId;
  // Auto-play only if the viewer was already watching, pressed play, or autoplay
  // is on — a switch during the initial load must not override "autoplay off".
  const shouldPlay = firstFrameDone || preStart.clicked || autoplayWanted() || !video.paused;
  void loadActiveSource().then(() => {
    if (gen !== memory.generation || memory.session?.playback.sourceId !== target) return;
    if (resumeAt) video.currentTime = resumeAt;
    if (shouldPlay) void video.play().catch(() => {});
    else { setPhase("ready"); showReady(); }
  });
}

// In-place recovery budget: a network error on a server that WAS playing is
// almost always the viewer's connection, not the server. Reload the same
// server at the same position (after the connection is back) before ever
// switching. Max 3 recoveries per 2 minutes.
let recoveries: number[] = [];

async function recoverInPlace(): Promise<boolean> {
  const now = Date.now();
  recoveries = recoveries.filter((t) => now - t < 120_000);
  if (recoveries.length >= 3 || !firstFrameDone) return false;
  recoveries.push(now);
  const resumeAt = video.currentTime;
  const gen = memory.generation;
  if (!navigator.onLine) {
    await new Promise<void>((r) => {
      const done = () => { window.removeEventListener("online", done); r(); };
      window.addEventListener("online", done);
      setTimeout(done, 60_000);
    });
  }
  await new Promise((r) => setTimeout(r, 1500));
  if (gen !== memory.generation) return true;
  console.warn(`[whyworry] recovering same server at ${resumeAt.toFixed(1)}s (attempt ${recoveries.length})`);
  await loadActiveSource();
  if (resumeAt > 1) video.currentTime = resumeAt;
  void video.play().catch(() => {});
  return true;
}

function onPlayerError(e: Event): void {
  const detail = (e as unknown as { detail?: { code?: number; severity?: number; category?: number } }).detail;
  console.warn(`[whyworry] player error code=${detail?.code} severity=${detail?.severity}`);
  if (isInterrupted(detail)) return;
  // Severity 1 = RECOVERABLE: shaka is already retrying (the [worker, origin]
  // uri pair). Only CRITICAL (2) errors mean this source is dead.
  if (detail?.severity === 1) return;
  if (mounting) return; // loadActiveSource's catch owns the decision
  // Category 1 = NETWORK. A server that already played gets recovered in place.
  if (detail?.category === 1 && firstFrameDone) {
    void recoverInPlace().then((ok) => { if (!ok) void failover("player error"); });
    return;
  }
  void failover("player error");
}

// A healthy minute of playback refills the failover budget (it used to never
// reset, so a few blips in a long episode ended on "unavailable").
let healthySince = 0;
video.addEventListener("timeupdate", () => {
  if (video.paused || video.readyState < 3) { healthySince = 0; return; }
  if (!healthySince) healthySince = Date.now();
  else if (Date.now() - healthySince > 60_000 && failoverCycles) { failoverCycles = 0; healthySince = Date.now(); }
});

// ─── Refresh before expiry (kit: renew grants on approach to expiry) ─────────

let refreshTimer: ReturnType<typeof setInterval>;
function scheduleRefresh(): void {
  clearInterval(refreshTimer);
  refreshTimer = setInterval(async () => {
    const session = memory.session;
    if (!session) return;
    const expires = new Date(session.playback.mediaGrantExpiresAt).getTime();
    const remaining = expires - Date.now();
    if (remaining > 30_000) return;
    const { data } = await fetchJson<PublicSession>(`/v1/sessions/${session.sessionId}/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    });
    if (data && "playback" in data && (data as PublicSession).playback) {
      memory.session = data;
      toast("Session renewed.");
    } else if (remaining < 0) {
      if (!(await recoverSession())) showUnavailable("Your session expired. Reload to watch.");
    }
  }, 10_000);
}

/**
 * 2026-11-04: a session can vanish (API restart wipes in-memory sessions, or
 * it expired). Instead of "Your session expired. Reload to watch.", get a new
 * ticket the same way boot did, redeem it, and continue from the same second.
 */
let recovering = false;
async function recoverSession(): Promise<boolean> {
  const content = memory.content;
  if (!content || recovering) return false;
  recovering = true;
  try {
    const known = viewerPosGen === memory.generation ? viewerPos : 0;
    const resumeAt = video.currentTime > 3 ? video.currentTime : known > 3 ? known : 0;
    const wasPlaying = !video.paused || firstFrameDone;
    const ticket = await requestTicket(content);
    const fresh = ticket ? await redeem(ticket) : null;
    if (!fresh) return false;
    console.warn(`[whyworry] session recovered at ${resumeAt.toFixed(1)}s`);
    memory.session = fresh;
    memory.generation += 1;
    failoverCycles = 0;
    refreshServerChrome();
    scheduleRefresh();
    void pollMoreSources();
    await raceYamiStart();
    await loadActiveSource();
    if (resumeAt) video.currentTime = resumeAt;
    if (wasPlaying) void video.play().catch(() => {});
    return true;
  } catch {
    return false;
  } finally {
    recovering = false;
  }
}

// ─── The approved player controls ────────────────────────────────────────────

let locked = false;
let currentSpeed = 1;

function togglePlay(): void {
  if (locked) return;
  if (phase !== "ready" && phase !== "playing" && phase !== "paused" && phase !== "buffering") return;
  if (video.paused || video.ended) {
    void video.play();
  } else {
    video.pause();
  }
}

function seek(delta: number): void {
  if (locked) return;
  if (!Number.isFinite(video.duration)) return;
  video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + delta));
  showSeekBurst(delta);
}

// ─── Seek burst (owner 2026-10-26): animated side ripple + cascading chevrons
// instead of a "+10 seconds" text box. Repeated seeks in a row accumulate
// ("10s" → "20s" → "30s") while the burst is still on screen.
const SEEK_CHEVRON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 5.5 15 12l-6.5 6.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const seekBursts: Partial<Record<"fwd" | "back", { el: HTMLElement; total: number; timer?: ReturnType<typeof setTimeout> }>> = {};

function seekBurstEl(side: "fwd" | "back"): { el: HTMLElement; total: number; timer?: ReturnType<typeof setTimeout> } {
  let b = seekBursts[side];
  if (!b) {
    const el = document.createElement("div");
    el.className = `ww-seek ww-seek-${side}`;
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      `<div class="ww-seek-ripple"></div><div class="ww-seek-core">` +
      `<div class="ww-seek-chevrons">${SEEK_CHEVRON}${SEEK_CHEVRON}${SEEK_CHEVRON}</div>` +
      `<div class="ww-seek-amount"></div></div>`;
    playerEl.appendChild(el);
    b = { el, total: 0 };
    seekBursts[side] = b;
  }
  return b;
}

function showSeekBurst(delta: number): void {
  const side = delta > 0 ? "fwd" : "back";
  const other = seekBursts[side === "fwd" ? "back" : "fwd"];
  if (other) {
    other.el.classList.remove("on");
    other.total = 0;
  }
  const b = seekBurstEl(side);
  b.total = b.el.classList.contains("on") ? b.total + Math.abs(delta) : Math.abs(delta);
  (b.el.querySelector(".ww-seek-amount") as HTMLElement).textContent = `${side === "fwd" ? "+" : "−"}${b.total}s`;
  // Restart the animation on every tap.
  b.el.classList.remove("on", "pulse");
  void b.el.offsetWidth;
  b.el.classList.add("on", "pulse");
  clearTimeout(b.timer);
  b.timer = setTimeout(() => {
    b.el.classList.remove("on", "pulse");
    b.total = 0;
  }, 750);
}

playBtn.addEventListener("click", () => {
  togglePlay();
  playerEl.focus({ preventScroll: true });
});
centerPlay.addEventListener("click", () => {
  togglePlay();
  playerEl.focus({ preventScroll: true });
});

document.querySelectorAll("[data-seek]").forEach((b) =>
  b.addEventListener("click", () => {
    seek(Number((b as HTMLElement).dataset.seek));
    playerEl.focus({ preventScroll: true });
  })
);

timeline.addEventListener("input", () => {
  if (locked) return;
  if (Number.isFinite(video.duration)) {
    video.currentTime = (Number(timeline.value) / 100) * video.duration;
  }
});

function updateMediaTime(): void {
  const duration = video.duration;
  if (Number.isFinite(duration) && duration > 0) {
    timeline.max = "100";
    timeline.value = String(Math.round((video.currentTime / duration) * 100));
    timeline.style.setProperty("--progress", `${(video.currentTime / duration) * 100}%`);
    durationEl.textContent = formatTime(duration);
    chapterLabel.innerHTML = `${
      formatTime(video.currentTime)
    } <span>${escapeHtml(memory.session?.title ?? "")}</span>`;
  }
  currentTimeEl.textContent = formatTime(video.currentTime);
  updateNwProgress();
}

const volumeInput = $<HTMLInputElement>("volume");
const muteBtn = $("mute-btn");
function updateVolume(): void {
  const muted = video.muted || video.volume === 0;
  volumeInput.value = String(muted ? 0 : video.volume);
  setIcon(muteBtn, muted ? "volume-x" : "volume");
  muteBtn.setAttribute("aria-label", muted ? "Unmute" : "Mute");
}
muteBtn.addEventListener("click", () => {
  if (locked) return;
  video.muted = !video.muted;
  updateVolume();
});
volumeInput.addEventListener("input", () => {
  video.volume = Number(volumeInput.value);
  video.muted = false;
  updateVolume();
});

async function toggleFullscreen(): Promise<void> {
  if (locked) return;
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (playerEl.requestFullscreen) await playerEl.requestFullscreen();
    else toast("Fullscreen is unavailable in this browser.");
  } catch {
    toast("Fullscreen is unavailable here.");
  }
}
$("fullscreen-btn").addEventListener("click", toggleFullscreen);
document.addEventListener("fullscreenchange", () => {
  const on = !!document.fullscreenElement;
  setIcon($("fullscreen-btn"), on ? "minimize" : "maximize");
  $("fullscreen-btn").setAttribute("aria-label", on ? "Exit fullscreen" : "Enter fullscreen");
});

function setLocked(next: boolean): void {
  locked = next;
  playerEl.classList.toggle("locked", next);
  $("unlock-btn").hidden = !next;
  closeMenu();
  if (next) {
    $("unlock-btn").focus({ preventScroll: true });
    toast("Controls locked. Tap the lock to unlock.");
  } else {
    $("lock-btn").focus({ preventScroll: true });
    feedback("Controls unlocked");
  }
}
$("lock-btn").addEventListener("click", () => setLocked(true));
$("unlock-btn").addEventListener("click", () => setLocked(false));

$("pip-btn").addEventListener("click", () => {
  const on = playerEl.classList.toggle("mini-player");
  $("pip-btn").setAttribute("aria-label", on ? "Close mini player" : "Mini player");
  if (on) toast("Mini player on. Use the same button to restore.");
});

// ─── Subtitles + quality (shaka track API, not raw video.textTracks) ────────
//
// shaka renders captions itself and exposes them via getTextTracks(); the
// native video.textTracks list stays empty for HLS SUBTITLES media, which is
// why the menu said "no caption track" while the manifest carried 1–22.
// Measured 2026-10-25: EREN megavid 1 track, YAMI 21 tracks, Nexa 0.

interface SubOption {
  key: string; // shaka track id (string) or "n<i>" for native element tracks
  name: string;
  lang: string;
}

function subtitleTracks(): SubOption[] {
  if (shakaPlayer) {
    return shakaPlayer.getTextTracks().map((t) => ({
      key: String(t.id),
      name: t.label || t.language || `Track ${t.id}`,
      lang: (t.language || "").toLowerCase(),
    }));
  }
  return Array.from({ length: video.textTracks.length }, (_, i) => {
    const t = video.textTracks[i]!;
    return { key: `n${i}`, name: t.label || t.language || `Track ${i + 1}`, lang: (t.language || "").toLowerCase() };
  });
}

function preferredSubtitle(): SubOption | null {
  const all = subtitleTracks();
  return all.find((t) => /^en/.test(t.lang) || /^english/i.test(t.name)) ?? all[0] ?? null;
}

function resetSubtitleUi(): void {
  subtitleValue.textContent = "Off";
  subtitleValue.dataset.index = "";
  captionPreview.hidden = true;
  document.querySelector(".cc-btn")?.classList.toggle("on", false);
}

function selectSubtitle(key: string): void {
  if (key === "off") {
    shakaPlayer?.setTextTrackVisibility(false);
    for (let i = 0; i < video.textTracks.length; i++) (video.textTracks[i] as TextTrack).mode = "hidden";
    resetSubtitleUi();
    return;
  }
  const opt = subtitleTracks().find((t) => t.key === key);
  if (!opt) return;
  if (shakaPlayer && !key.startsWith("n")) {
    const track = shakaPlayer.getTextTracks().find((t) => String(t.id) === key);
    if (track) {
      shakaPlayer.selectTextTrack(track);
      shakaPlayer.setTextTrackVisibility(true);
    }
  } else {
    const idx = Number(key.slice(1));
    for (let i = 0; i < video.textTracks.length; i++) {
      (video.textTracks[i] as TextTrack).mode = i === idx ? "showing" : "hidden";
    }
  }
  subtitleValue.textContent = opt.name;
  subtitleValue.dataset.index = key;
  document.querySelector(".cc-btn")?.classList.toggle("on", true);
}

/** Anime is watched subbed: turn English (or the only track) on by default. */
function applyDefaultSubtitles(): void {
  if (memory.content?.type !== "anime") return;
  const pref = preferredSubtitle();
  if (pref) selectSubtitle(pref.key);
}

function qualityHeights(): number[] {
  if (!shakaPlayer) return [];
  const hs = new Set<number>();
  for (const t of shakaPlayer.getVariantTracks()) if (t.height) hs.add(t.height);
  return [...hs].sort((a, b) => b - a);
}

function isAutoQuality(): boolean {
  return !shakaPlayer || shakaPlayer.getConfiguration().abr.enabled !== false;
}

function activeHeight(): number | null {
  return shakaPlayer?.getVariantTracks().find((t) => t.active)?.height ?? null;
}

function qualityLabel(): string {
  const h = activeHeight();
  if (isAutoQuality()) return h ? `Auto · ${h}p` : "Auto";
  return h ? `${h}p` : "Auto";
}

function selectQuality(value: string): void {
  if (!shakaPlayer) return;
  if (value === "auto") {
    shakaPlayer.configure({ abr: { enabled: true } });
    return;
  }
  const h = Number(value);
  // Same height can exist per audio group — keep the active audio language.
  const activeLang = shakaPlayer.getVariantTracks().find((t) => t.active)?.language;
  const pick =
    shakaPlayer.getVariantTracks().find((t) => t.height === h && t.language === activeLang) ??
    shakaPlayer.getVariantTracks().find((t) => t.height === h);
  if (!pick) return;
  shakaPlayer.configure({ abr: { enabled: false } });
  shakaPlayer.selectVariantTrack(pick, /* clearBuffer */ true);
}

// ─── Audio: in-stream languages + anime Sub/Dub ─────────────────────────────

function currentTrack(): "sub" | "dub" {
  return (memory.content as { track?: string } | null)?.track === "dub" ? "dub" : "sub";
}

function languageName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function audioLanguages(): { lang: string; label: string; active: boolean }[] {
  if (!shakaPlayer) return [];
  const out = new Map<string, { lang: string; label: string; active: boolean }>();
  for (const t of shakaPlayer.getVariantTracks()) {
    const lang = t.language || "und";
    const prev = out.get(lang);
    const label = lang === "und" ? "Original" : languageName(lang);
    out.set(lang, { lang, label, active: (prev?.active ?? false) || t.active });
  }
  return [...out.values()];
}

function audioLabel(): string {
  if (memory.content?.type === "anime") return currentTrack() === "dub" ? "English · Dub" : "Japanese · Sub";
  return audioLanguages().find((l) => l.active)?.label ?? "Original";
}

/**
 * 2026-11-04 (viewer request): remember the viewer's Sub/Dub choice. Partner
 * sites (9ime) always open the embed with track=sub, so a dub viewer had to
 * switch on every episode. Saved only when the viewer switches in the menu.
 */
const AUDIO_PREF_KEY = "ww-audio-pref";
function savedAudioPref(): "sub" | "dub" | null {
  try {
    const v = localStorage.getItem(AUDIO_PREF_KEY);
    return v === "dub" || v === "sub" ? v : null;
  } catch {
    return null;
  }
}
function saveAudioPref(track: "sub" | "dub"): void {
  try { localStorage.setItem(AUDIO_PREF_KEY, track); } catch { /* storage blocked */ }
}

/** Same handshake as boot: parent embed issues a ticket; dev self-issues. */
async function requestTicket(content: ContentSelector): Promise<string | null> {
  const pg = playgroundParent();
  if (pg) {
    const t = await playgroundTicket(content, pg);
    if (t) return t;
  }
  if (!pg && window.parent && window.parent !== window) {
    const t = await new Promise<string | null>((resolve) => {
      const timeout = setTimeout(() => resolve(null), 4000);
      window.addEventListener("message", function onMessage(ev: MessageEvent) {
        const data = ev.data as Record<string, unknown> | null;
        if (ev.source === window.parent && data && typeof data === "object" && typeof data.ticket === "string") {
          window.removeEventListener("message", onMessage);
          clearTimeout(timeout);
          resolve(String(data.ticket));
        }
      });
      // "*" : the parent is the partner site (another origin); the payload is just the public content selector. The REPLY is what we gate (source must be our parent).
      window.parent.postMessage({ type: "whyworry:ready", content }, "*");
    });
    if (t) return t;
  }
  return devSelfTicket(content);
}

/**
 * Sub ⇄ Dub: a fresh session for the other track, keeping the viewer's spot.
 * If that track has no source, stay on the current one (never a dead end).
 */
async function switchTrack(track: "sub" | "dub"): Promise<void> {
  const content = memory.content;
  if (!content || content.type !== "anime") return;
  const next = { ...content, track } as ContentSelector;
  const resumeAt = video.currentTime;
  showSwitch(track === "dub" ? "English Dub" : "Japanese", "SWITCHING AUDIO TO");
  const ticket = await requestTicket(next);
  const session = ticket ? await redeem(ticket) : null;
  if (!session) {
    switchEl.classList.remove("show");
    toast(track === "dub" ? "No English dub for this episode yet." : "Couldn't switch audio right now.");
    return;
  }
  saveAudioPref(track);
  memory.session = session;
  memory.content = next;
  memory.generation += 1;
  failoverCycles = 0;
  ($("audio-value") as HTMLElement).textContent = audioLabel();
  await loadActiveSource();
  if (resumeAt > 3) video.currentTime = resumeAt;
  void video.play().catch(() => {});
  feedback(`Audio: ${audioLabel()}`);
  void pollMoreSources();
  void loadSkipTimes();
}

// ─── Server-switch reveal ───────────────────────────────────────────────────
const switchEl = $("ww-switch");
let switchShownAt = 0;
function showSwitch(name: string, eyebrow = "SWITCHING TO"): void {
  const n = $("ww-switch-name");
  n.textContent = name;
  n.setAttribute("data-text", name);
  (switchEl.querySelector(".ww-switch-eyebrow") as HTMLElement).textContent = eyebrow;
  // Restart the CSS animations on every switch.
  switchEl.classList.remove("show");
  void switchEl.offsetWidth;
  switchEl.classList.add("show");
  switchShownAt = Date.now();
  startSwitchLines();
  hideNowWatching();
}
function hideSwitch(): void {
  if (!switchEl.classList.contains("show")) return;
  // Let the reveal land (≥1.6 s) even when the new server is instant.
  const wait = Math.max(0, 900 - (Date.now() - switchShownAt));
  setTimeout(() => {
    switchEl.classList.remove("show");
    stopSwitchLines();
  }, wait);
}

// Rotating lines on the switch screen (anime / movie vibe). Random start,
// a new line every 2.4 s while the screen is up.
const SWITCH_LINES = [
  "Take a deep breath — it'll be back in a blink.",
  "Every hero gets a comeback arc.",
  "Plot armor engaged. Reconnecting you…",
  "Even legends need a quick power-up.",
  "Switching reels — don't blink.",
  "Hold the line. The best scene is next.",
  "Charging up… 3… 2… 1…",
  "Intermission ends in a heartbeat.",
  "The story never stops. Neither do we.",
  "Finding you a faster road to the action.",
];
let switchLineTimer: ReturnType<typeof setInterval> | undefined;
function startSwitchLines(): void {
  const el = document.getElementById("ws-line");
  if (!el) return;
  let i = Math.floor(Math.random() * SWITCH_LINES.length);
  el.classList.remove("swap");
  el.textContent = SWITCH_LINES[i]!;
  clearInterval(switchLineTimer);
  switchLineTimer = setInterval(() => {
    el.classList.add("swap");
    setTimeout(() => {
      i = (i + 1) % SWITCH_LINES.length;
      el.textContent = SWITCH_LINES[i]!;
      el.classList.remove("swap");
    }, 350);
  }, 2400);
}
function stopSwitchLines(): void {
  clearInterval(switchLineTimer);
}
video.addEventListener("playing", hideSwitch);

// ─── Premium loader + auto-hiding chrome ────────────────────────────────────

const loaderEl = $("ww-loader");
const loaderLine = $("ww-loader-line");
let firstFrameDone = false;
const LOADER_LINES = [
  "Summoning the fastest server…",
  "Racing every server for you…",
  "Lining up your episode…",
  "Warming up the projector…",
  "Grab your snacks — almost showtime…",
];
let loaderLineIdx = 0;
const loaderTicker = setInterval(() => {
  if (loaderEl.classList.contains("done")) return clearInterval(loaderTicker);
  loaderLine.classList.add("swap");
  setTimeout(() => {
    loaderLineIdx = (loaderLineIdx + 1) % LOADER_LINES.length;
    loaderLine.textContent = LOADER_LINES[loaderLineIdx]!;
    loaderLine.classList.remove("swap");
  }, 350);
}, 1800);

function fillLoader(): void {
  const s = memory.session;
  const c = memory.content;
  const logo = s?.meta?.logoUrl;
  const logoEl = $<HTMLImageElement>("ww-loader-logo");
  if (logo) {
    logoEl.src = logo;
    logoEl.hidden = false;
    $("ww-loader-title").hidden = true;
  } else {
    logoEl.hidden = true;
    $("ww-loader-title").hidden = false;
    $("ww-loader-title").textContent = s?.meta?.name ?? s?.title ?? "";
  }
  $("ww-loader-sub").textContent = c ? contentDescription(c).toUpperCase() : "";
  const art = s?.meta?.posterUrl ?? s?.posterUrl;
  if (art) $("ww-loader-bg").style.backgroundImage = `url("${art}")`;
}

function finishLoader(): void {
  if (firstFrameDone) return;
  firstFrameDone = true;
  loaderEl.classList.add("done");
  pokeChrome();
}

let idleTimer: ReturnType<typeof setTimeout> | undefined;
function pokeChrome(): void {
  playerEl.classList.remove("ww-idle");
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    // Keep chrome up while a menu is open or before playback exists.
    if (!menuEl.hidden || !firstFrameDone || phase === "unavailable" || phase === "error") return pokeChrome();
    playerEl.classList.add("ww-idle");
  }, 2000);
}
for (const ev of ["mousemove", "pointerdown", "touchstart", "keydown", "wheel"]) {
  playerEl.addEventListener(ev, pokeChrome, { passive: true });
}
playerEl.addEventListener("mouseleave", () => {
  if (firstFrameDone && menuEl.hidden) {
    clearTimeout(idleTimer);
    playerEl.classList.add("ww-idle");
  }
});

// Top-right server button: hover previews the server list right there.
const serverBtn = $("ww-server-btn");
let hoverClose: ReturnType<typeof setTimeout> | undefined;
serverBtn.addEventListener("mouseenter", () => {
  clearTimeout(hoverClose);
  if (openMenuType !== "servers") openMenu("servers", true);
});
for (const el of [serverBtn, menuEl]) {
  el.addEventListener("mouseleave", () => {
    if (!menuEl.classList.contains("ww-top")) return;
    hoverClose = setTimeout(() => closeMenu(), 450);
  });
  el.addEventListener("mouseenter", () => clearTimeout(hoverClose));
}

// ─── Menu system (approved markup + menuContent shape) ───────────────────────

const menuOption = (label: string, action: string, value: string, selected: boolean, detail = "") =>
  `<button class="menu-option ${selected ? "selected" : ""}" data-action="${action}" data-value="${escapeHtml(value)}"><span>${escapeHtml(label)}</span><span>${escapeHtml(detail)} ${selected ? icon("check") : ""}</span></button>`;

function menuContent(type: string): { title: string; items: string; note: string } {
  const session = memory.session;
  const playback = session?.playback;
  if (type === "servers") {
    const names = playback ? serverNames(playback.sources) : new Map<string, string>();
    const items =
      playback?.sources
        .map((s) => menuOption(names.get(s.sourceId) ?? "Server", "server", s.sourceId, s.sourceId === playback.sourceId, s.sourceId === playback.sourceId ? "Playing" : ""))
        .join("") ?? "";
    return { title: "Servers", items, note: `${playback?.sources.length ?? 0} servers ready · switch anytime, your spot is kept.` };
  }
  if (type === "audio") {
    const items: string[] = [];
    if (memory.content?.type === "anime") {
      const cur = currentTrack();
      items.push(menuOption("Japanese", "track", "sub", cur === "sub", "Subbed"));
      items.push(menuOption("English", "track", "dub", cur === "dub", "Dubbed"));
    }
    const langs = audioLanguages();
    if (langs.length > 1) items.push(...langs.map((l) => menuOption(l.label, "audiolang", l.lang, l.active, "In stream")));
    return {
      title: "Audio",
      items: items.join("") || menuOption("Original", "noop", "", true),
      note: memory.content?.type === "anime" ? "Dub availability depends on the episode." : "Audio tracks exposed by this server.",
    };
  }
  if (type === "speed") {
    return {
      title: "Playback speed",
      items: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
        .map((s) => menuOption(`${s}×${s === 1 ? " · Normal" : ""}`, "speed", String(s), currentSpeed === s))
        .join(""),
      note: "Changes playback speed.",
    };
  }
  if (type === "subtitles") {
    const tracks = subtitleTracks();
    const current = subtitleValue.dataset.index ?? "";
    return {
      title: "Subtitles & captions",
      items:
        menuOption("Off", "subtitle", "off", current === "") +
        tracks.map((t) => menuOption(t.name, "subtitle", t.key, t.key === current)).join(""),
      note: tracks.length ? "Caption tracks exposed by the active source." : "This source exposes no caption track.",
    };
  }
  if (type === "quality") {
    const heights = qualityHeights();
    const auto = isAutoQuality();
    const active = activeHeight();
    return {
      title: "Quality",
      items:
        menuOption("Auto", "quality", "auto", auto, auto && active ? `${active}p` : "") +
        heights.map((h) => menuOption(`${h}p`, "quality", String(h), !auto && h === active, h >= 720 ? "HD" : "")).join(""),
      note: heights.length > 1 ? "Auto adapts to your connection." : "This server offers a single quality.",
    };
  }
  return {
    title: "Player settings",
    items: [
      ["Quality", qualityLabel(), "quality"],
      ["Audio", audioLabel(), "audio"],
      ["Playback speed", `${currentSpeed}×`, "speed"],
      ["Subtitles", subtitleValue.textContent ?? "Off", "subtitles"],
      ["Server", memory.session ? serverLabel(memory.session.playback) : "Auto", "servers"],
    ]
      .map(
        ([label, value, panel]) =>
          `<button class="menu-option" data-panel="${panel}"><span>${label}</span><span>${value}${icon("chevron-right")}</span></button>`
      )
      .join(""),
    note: "Tuned for the smoothest stream on your connection.",
  };
}

let openMenuType: string | null = null;
function openMenu(type: string, top = menuEl.classList.contains("ww-top")): void {
  if (locked) return void toast("Unlock the player to change settings.");
  menuEl.classList.toggle("ww-top", top);
  pokeChrome();
  openMenuType = type;
  const content = menuContent(type);
  menuEl.innerHTML = `<div class="menu-heading"><h3>${escapeHtml(content.title)}</h3><button data-close-menu aria-label="Close menu">${icon("x")}</button></div><div class="menu-options">${content.items}</div><div class="menu-description">${escapeHtml(content.note)}</div>`;
  menuEl.hidden = false;
  menuEl.querySelector("button")?.focus({ preventScroll: true });
}
function closeMenu(): void {
  menuEl.hidden = true;
  menuEl.classList.remove("ww-top");
  openMenuType = null;
}
document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  const panel = target.closest<HTMLElement>("[data-panel]");
  if (panel) {
    e.stopPropagation();
    const t = panel.dataset.panel ?? "";
    if (openMenuType === t && !panel.closest("#player-menu")) closeMenu();
    else openMenu(t, panel.closest("#player-menu") ? undefined : !!panel.closest("#ww-server-btn"));
    return;
  }
  if (target.closest("[data-close-menu]")) {
    closeMenu();
    playerEl.focus({ preventScroll: true });
    return;
  }
  const action = target.closest<HTMLElement>("[data-action]");
  if (action) {
    handleSetting(action.dataset.action ?? "", action.dataset.value ?? "");
    return;
  }
  if (!menuEl.contains(target)) closeMenu();
});

async function handleSetting(action: string, value: string): Promise<void> {
  if (locked) return;
  if (action === "speed") {
    currentSpeed = Number(value) || 1;
    video.playbackRate = currentSpeed;
    if (speedBtn) speedBtn.textContent = `${currentSpeed}×`;
    feedback(`Playback ${currentSpeed}×`);
  }
  if (action === "server") {
    const session = memory.session;
    if (session && value !== session.playback.sourceId) {
      const picked = session.playback.sources.find((s) => s.sourceId === value);
      if (picked?.onDemand) {
        closeMenu();
        feedback(`Loading ${(picked.label.split(" · ")[0] ?? picked.label).trim()}…`);
      }
      const { status, data } = await fetchJson<{ playback?: PublicPlayback }>(
        `/v1/sessions/${session.sessionId}/source`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sourceId: value, accessToken: session.accessToken, manual: true }),
        }
      );
      if (picked?.onDemand && (status !== 200 || !data?.playback)) {
        feedback(`${(picked.label.split(" · ")[0] ?? picked.label).trim()} has nothing for this episode`);
      }
      if (status === 200 && data?.playback) {
        const resumeAt = video.currentTime > 3 ? video.currentTime : 0;
        session.playback = data.playback;
        failoverCycles = 0; // a manual pick restarts the failover budget
        closeMenu();
        showSwitch(serverLabel(session.playback));
        await loadActiveSource();
        if (resumeAt) video.currentTime = resumeAt; // keep the viewer's spot
        void video.play().catch(() => {});
        feedback(`Now on ${serverLabel(session.playback)}`);
      }
    }
  }
  if (action === "subtitle") {
    selectSubtitle(value);
    feedback(`Subtitles: ${subtitleValue.textContent}`);
  }
  if (action === "quality") {
    selectQuality(value);
    feedback(`Quality: ${qualityLabel()}`);
  }
  if (action === "audiolang" && shakaPlayer) {
    shakaPlayer.selectAudioLanguage(value);
    feedback(`Audio: ${audioLabel()}`);
  }
  if (action === "track" && (value === "sub" || value === "dub") && value !== currentTrack()) {
    closeMenu();
    void switchTrack(value);
    return;
  }
  closeMenu();
  playerEl.focus({ preventScroll: true });
}

// Keyboard: the approved shortcut set (play/seek/volume/mute/fullscreen/l/c)
playerEl.addEventListener("keydown", (e) => {
  if (
    (e.target as HTMLElement).matches("input,button") &&
    ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)
  ) {
    return;
  }
  if (locked && e.key.toLowerCase() !== "l") return;
  const key = e.key.toLowerCase();
  if ([" ", "arrowleft", "arrowright", "arrowup", "arrowdown", "m", "f", "c", "l", "escape"].includes(key)) {
    e.preventDefault();
  }
  if (key === " ") togglePlay();
  else if (key === "arrowleft") seek(-10);
  else if (key === "arrowright") seek(10);
  else if (key === "arrowup" || key === "arrowdown") {
    video.volume = Math.max(0, Math.min(1, video.volume + (key === "arrowup" ? 0.1 : -0.1)));
    updateVolume();
    feedback(`Volume ${Math.round(video.volume * 100)}%`);
  } else if (key === "m") {
    video.muted = !video.muted;
    updateVolume();
  } else if (key === "f") void toggleFullscreen();
  else if (key === "l") setLocked(!locked);
  else if (key === "c") void handleSetting("subtitle", subtitleValue.dataset.index ? "off" : (preferredSubtitle()?.key ?? "off"));
  else if (key === "escape") closeMenu();
});

// Double-tap seek (mouse) — mirrors the kit's touch behaviour.
let lastTap = 0;
// Single tap/click on the picture = play/pause (owner 2026-10-25). Delayed a
// beat so a double-tap seek doesn't also toggle.
let tapTimer: ReturnType<typeof setTimeout> | undefined;
playerEl.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  if (locked || t.closest("button,input,a,.player-menu,.player-bottom,.player-top,.unlock-control,.ww-loader")) return;
  if (!menuEl.hidden) return closeMenu();
  clearTimeout(tapTimer);
  tapTimer = setTimeout(() => togglePlay(), 220);
});
playerEl.addEventListener("dblclick", (e) => {
  clearTimeout(tapTimer);
  const target = e.target as HTMLElement;
  if (target.closest("button,input,.player-menu") || locked || Date.now() - lastTap < 500) return;
  const rect = playerEl.getBoundingClientRect();
  seek(e.clientX - rect.left < rect.width / 2 ? -10 : 10);
});
playerEl.addEventListener("touchstart", () => {
  lastTap = Date.now();
});

// ─── Video element events → FSM ──────────────────────────────────────────────

video.addEventListener("loadedmetadata", () => {
  updateMediaTime();
  setPhase("ready");
});
video.addEventListener("play", () => {
  setPhase("playing");
  playHint.hidden = true;
});
// Reveal the video surface only once a real frame exists (audio-only or a
// still-loading source keeps the poster up instead of a black box).
const revealFrame = () => {
  if (video.videoWidth > 0) playerEl.classList.add("has-frame");
};
video.addEventListener("loadeddata", revealFrame);
video.addEventListener("resize", revealFrame);
video.addEventListener("playing", () => {
  revealFrame();
  setPhase("playing");
  showNowWatching(true);
});
video.addEventListener("pause", () => {
  if (phase === "playing" || phase === "buffering") setPhase("paused");
  showNowWatching(false);
});
video.addEventListener("ended", () => {
  setPhase("ended");
  hideNowWatching();
});
video.addEventListener("waiting", () => {
  if (phase === "playing") setPhase("buffering");
});
video.addEventListener("canplay", () => {
  if (phase === "buffering") setPhase("playing");
});
video.addEventListener("timeupdate", updateMediaTime);
video.addEventListener("volumechange", updateVolume);
video.addEventListener("error", () => {
  // Unloading/swapping sources can fire a transient element error; the
  // mount path decides those. Only a real error on a mounted source fails over.
  if (mounting || !video.currentSrc) return;
  void failover("media element error");
});

// ─── Boot: handshake → ticket → session → mount ──────────────────────────────

function contentDescription(c: ContentSelector): string {
  if (c.type === "tv") return `S${String(c.season).padStart(2, "0")} · E${String(c.episode).padStart(2, "0")}`;
  if (c.type === "anime") return `Episode ${String(c.episode).padStart(2, "0")}`;
  return "Feature presentation";
}

async function start(content: ContentSelector | null): Promise<void> {
  setPhase("authorizing");
  filmEyebrow.textContent = content ? `${labelOf(content.type)} · ${contentDescription(content)}` : "NO CONTENT SELECTED";
  $("ww-loader-sub").textContent = content ? contentDescription(content).toUpperCase() : "";
  if (!content) {
    showUnavailable("No content selector in this embed URL.");
    return;
  }

  // Viewer's saved Dub preference wins over the partner's default "sub".
  // If this episode has no dub, we fall back to the original below.
  const requested = content;
  if (content.type === "anime" && (content as { track?: string }).track !== "dub" && savedAudioPref() === "dub") {
    content = { ...content, track: "dub" } as ContentSelector;
  }

  // 0) Website playground: rate-limited public ticket, no handshake wait.
  const pg = playgroundParent();
  const pgTicket = pg ? await playgroundTicket(content, pg) : null;
  let embedOrigin: string | null = pg;

  // 1) The kit handshake: ready → ticket from our parent (exact-origin rule).
  const ticket = pgTicket ?? await new Promise<string | null>((resolve) => {
    if (pg) return resolve(null); // playground already tried; fall through to dev
    if (!window.parent || window.parent === window) return resolve(null); // top-level dev page
    const timeout = // Partner backends mint the ticket server-side; give them time (a cold
      // route / first request can take several seconds), then fall back.
      setTimeout(() => resolve(null), 8000);
    window.addEventListener("message", function onMessage(ev: MessageEvent) {
      const data = ev.data as Record<string, unknown> | null;
      if (ev.source === window.parent && data && typeof data === "object" && typeof data.ticket === "string") {
        window.removeEventListener("message", onMessage);
        embedOrigin = ev.origin && ev.origin !== "null" ? ev.origin : null;
        clearTimeout(timeout);
        resolve(String(data.ticket));
      }
    });
    // "*" : the parent is the partner site (another origin); the payload is just the public content selector. The REPLY is what we gate (source must be our parent).
      window.parent.postMessage({ type: "whyworry:ready", content }, "*");
  });

  // 2) Dev fallback: self-issue a ticket for OUR OWN test page (localhost only;
  //    /v1/dev routes don't exist in production builds).
  let finalTicket = ticket;
  if (!finalTicket) {
    finalTicket = await devSelfTicket(content);
    // (dev self-ticket: silent — no "DEV PREVIEW" label or toast in the UI)
  }

  setPhase("resolving");
  if (!finalTicket) {
    // 2026-10-30: opened directly (no partner site, no playground) → a clear
    // explanation instead of an endless "Resolving".
    const standalone = window.parent === window;
    showUnavailable(
      standalone
        ? "This player runs inside partner websites and can't be opened on its own. To play your own video links, use the free WhyWorry Player at whyworry.site."
        : "This site didn't send a playback ticket. The site owner needs a WhyWorry partner key, available at whyworry.site."
    );
    serverValue.textContent = "Not available";
    filmTitle.textContent = standalone ? "WhyWorry Player" : "Playback not authorised";
    filmMeta.textContent = standalone ? "Embed-only player · whyworry.site" : "Missing partner ticket";
    if (standalone) {
      const a = document.createElement("a");
      a.href = "https://whyworry.site/saas";
      a.target = "_top";
      a.rel = "noopener";
      a.textContent = "Open whyworry.site";
      a.style.cssText = "display:inline-block;margin-top:14px;padding:10px 18px;border-radius:999px;background:var(--accent,#ff6b35);color:#fff;font-weight:600;text-decoration:none;pointer-events:auto";
      playHint.appendChild(document.createElement("br"));
      playHint.appendChild(a);
    }
    return;
  }

  // Staged progress: a full resolve walks fast providers, then the slow
  // rescue tier (measured up to ~65s). Tell the truth so the wait reads as
  // work, not as a hang.
  const resolveStarted = Date.now();
  const resolveTicker = setInterval(() => {
    const elapsed = (Date.now() - resolveStarted) / 1000;
    if (elapsed > 40) loadingText.textContent = "Verifying the slowest servers — almost done…";
    else if (elapsed > 12) loadingText.textContent = "Slower source servers still working…";
  }, 1000);

  embedOriginMemo = embedOrigin;
  let session = await redeem(finalTicket, embedOrigin).finally(() => clearInterval(resolveTicker));
  if (content !== requested && redeemError !== "ORIGIN_NOT_ALLOWED" && !session?.playback?.sources?.length) {
    // Saved Dub preference, but no dub for this episode: play the original track.
    const t = await requestTicket(requested);
    const s = t ? await redeem(t, embedOrigin) : null;
    if (s) {
      session = s;
      content = requested;
      toast("No English dub for this episode yet. Playing Japanese.");
    }
  }
  if (!session && redeemError === "ORIGIN_NOT_ALLOWED") {
    showUnavailable("This website isn't allowed to use this WhyWorry player. The site owner can add the domain in their WhyWorry partner dashboard at whyworry.site.");
    serverValue.textContent = "Not available";
    return;
  }
  if (!session) {
    showUnavailable("This title isn't available right now. Try again shortly.");
    return;
  }

  memory.session = session;
  memory.content = content;
  memory.generation += 1;
  populateNowWatching();
  bootAds((session as { ads?: AdsPlan | null }).ads ?? null);

  filmTitle.textContent = session.title || "Untitled";
  filmMeta.textContent = contentDescription(content);
  fillLoader();
  refreshServerChrome();
  ($("audio-value") as HTMLElement).textContent = audioLabel();
  serverIndicator.textContent = session.playback.sources.length > 1 ? "AUTO FAILOVER" : "SOLO SOURCE";
  subtitleValue.textContent = "Off";
  void (session.posterUrl ? ($("cinema-art") as HTMLImageElement).setAttribute("src", session.posterUrl) : null);
  scheduleRefresh();

  // Learn about late-joining servers from the start, not only after the first
  // source finished loading (a hanging source never finishes).
  void pollMoreSources();
  // 2026-11-04 (owner): the fastest WORKING YAMI plays, decided in THIS browser.
  await raceYamiStart();
  await loadActiveSource();
  if (autoplayWanted()) {
    video.play().then(
      () => { preStart.ap = "ok"; },
      (e: unknown) => {
        // Browser autoplay policy refused: show the premium ready screen.
        preStart.ap = "blocked";
        preStart.rj = (e as { name?: string } | null)?.name ?? "rejected";
        setPhase("ready");
        showReady();
      },
    );
  } else {
    preStart.ap = "off";
    setPhase("ready");
    showReady();
  }
  void loadSkipTimes();
}

/** Player ads (admin47 → Ads). Started once per page; never allowed to break playback. */
let adsBooted = false;
function bootAds(plan: AdsPlan | null): void {
  if (adsBooted || !plan || !Array.isArray(plan.slots) || plan.slots.length === 0) return;
  adsBooted = true;
  try {
    // The ad's click must also start the video (owner 2026-11-04): only when
    // it has never played, so a viewer's pause/seek is never overridden.
    const startIfNeverPlayed = () => {
      if (video.paused && !video.ended && video.played.length === 0) togglePlay();
    };
    const ctl = startAds(plan, plan.test ? (m) => console.info(`[ww-ads] ${m}`) : undefined, startIfNeverPlayed);
    if (plan.test) (window as unknown as { __wwAds?: unknown }).__wwAds = ctl;
  } catch (e) {
    console.warn("[ww-ads] disabled:", e);
  }
}

/** Embed `?autoplay=0` wins; otherwise the server's setting (admin panel). */
function autoplayWanted(): boolean {
  const q = new URLSearchParams(location.search).get("autoplay");
  if (q === "0" || q === "false") return false;
  if (q === "1" || q === "true") return true;
  return (memory.session as { autoplay?: boolean } | null)?.autoplay !== false;
}

// ─── Ready screen: key art, logo above play, details below ──────────────────
const readyEl = $("ww-ready");
function showReady(): void {
  const s = memory.session;
  if (!s || phase === "playing") return;
  const m = s.meta;
  const art = m?.posterUrl ?? s.posterUrl;
  if (art) readyEl.style.backgroundImage = `url("${art}")`;
  const logo = $<HTMLImageElement>("ww-ready-logo");
  if (m?.logoUrl) {
    logo.src = m.logoUrl;
    logo.alt = m.name ?? "";
    logo.hidden = false;
    $("ww-ready-title").hidden = true;
  } else {
    logo.hidden = true;
    $("ww-ready-title").hidden = false;
    $("ww-ready-title").textContent = m?.name ?? s.title ?? "";
  }
  const ep = memory.content ? contentDescription(memory.content) : "";
  $("ww-ready-ep").textContent = [ep.toUpperCase(), m?.episodeName ?? ""].filter(Boolean).join("  ·  ");
  const parts: string[] = [];
  if (m?.rating) parts.push(`<span>${escapeHtml(m.rating)}</span>`);
  if (m?.year) parts.push(`<span>${m.year}</span>`);
  for (const g of (m?.genres ?? []).slice(0, 3)) parts.push(`<span>${escapeHtml(g)}</span>`);
  $("ww-ready-badges").innerHTML = parts.join("");
  $("ww-ready-overview").textContent = m?.overview ?? "";
  $("ww-ready-play").appendChild(centerPlay);
  centerPlay.hidden = false;
  hideNowWatching();
  playerEl.classList.add("ww-ready-on");
  readyEl.setAttribute("aria-hidden", "false");
}
function hideReady(): void {
  if (!playerEl.classList.contains("ww-ready-on")) return;
  playerEl.classList.remove("ww-ready-on");
  readyEl.setAttribute("aria-hidden", "true");
  readyEl.after(centerPlay); // back to its normal spot for pauses
}
video.addEventListener("play", hideReady);

/**
 * Fast start plays the FIRST verified server; the rest keep resolving on the
 * server and are merged into the session. Pick them up for the Servers menu
 * (and failover) without touching the active playback.
 */
// ─── Skip Intro / Skip Outro (AniSkip via /v1/sessions/:id/skip) ────────────
// Shown while the playhead is inside the episode's OP/ED interval; one click
// seeks to its end. No data for an episode = no button (common for new eps).
interface SkipRange { start: number; end: number }
let skipTimes: { intro: SkipRange | null; outro: SkipRange | null } | null = null;
const skipBtn = document.getElementById("ww-skip") as HTMLButtonElement | null;

async function loadSkipTimes(): Promise<void> {
  skipTimes = null;
  if (skipBtn) skipBtn.hidden = true;
  const session = memory.session;
  if (!session) return;
  const gen = memory.generation;
  const { status, data } = await fetchJson<{ intro: SkipRange | null; outro: SkipRange | null }>(
    `/v1/sessions/${session.sessionId}/skip`,
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ accessToken: session.accessToken }) }
  );
  if (gen !== memory.generation || status !== 200 || !data) return;
  skipTimes = data;
  updateSkipButton();
}

function activeSkip(): { kind: "intro" | "outro"; range: SkipRange } | null {
  if (!skipTimes) return null;
  const t = video.currentTime;
  const inside = (r: SkipRange | null) => !!r && t >= r.start - 0.5 && t < r.end - 1;
  if (inside(skipTimes.outro)) return { kind: "outro", range: skipTimes.outro! };
  if (inside(skipTimes.intro)) return { kind: "intro", range: skipTimes.intro! };
  return null;
}

function updateSkipButton(): void {
  if (!skipBtn) return;
  const a = activeSkip();
  if (!a) {
    if (!skipBtn.hidden) skipBtn.hidden = true;
    return;
  }
  const text = a.kind === "intro" ? "Skip Intro" : "Skip Outro";
  if (skipBtn.textContent !== text) skipBtn.textContent = text;
  if (skipBtn.hidden) skipBtn.hidden = false;
}

skipBtn?.addEventListener("click", (e) => {
  e.stopPropagation(); // never toggle pause via the tap-to-pause surface
  const a = activeSkip();
  if (!a) return;
  const dur = Number.isFinite(video.duration) ? video.duration : Infinity;
  video.currentTime = Math.min(a.range.end, dur - 0.5);
  skipBtn.hidden = true;
  void video.play().catch(() => {});
});
video.addEventListener("timeupdate", updateSkipButton);
video.addEventListener("seeked", updateSkipButton);

// ─── Analytics heartbeat (Phase 2): every 30 s + on tab close ───────────────
// Real numbers only: playing state, position, buffering events, errors and the
// current height since the last beat. No content, no personal data.
let beatBuffering = 0;
let beatErrors = 0;
let firstFrameAtMs: number | null = null;
const pageStartedAt = performance.now();
// Only real mid-playback stalls: not the initial load, not a viewer's seek.
video.addEventListener("waiting", () => { if (firstFrameAtMs !== null && !video.seeking) beatBuffering += 1; });
video.addEventListener("error", () => {
  beatErrors += 1;
  preStart.err = `m${video.error?.code ?? 0}`;
});
video.addEventListener("playing", () => {
  if (firstFrameAtMs === null) firstFrameAtMs = Math.round(performance.now() - pageStartedAt);
}, { once: false });
// Any press of play before the first frame (a real click, not autoplay).
video.addEventListener("play", () => { if (firstFrameAtMs === null && navigator.userActivation?.isActive) preStart.clicked = true; });
document.addEventListener("pointerdown", () => { if (firstFrameAtMs === null && (phase === "ready" || phase === "paused")) preStart.clicked = true; }, true);

/** Until the first frame: where the player is and why (sent with every beat). */
function preStartReport(): Record<string, unknown> | undefined {
  if (firstFrameAtMs !== null) return undefined;
  return {
    stage: phase,
    ms: Math.round(performance.now() - pageStartedAt),
    ap: preStart.ap,
    rj: preStart.rj,
    err: preStart.err,
    vis: document.visibilityState === "hidden" ? "hidden" : "visible",
    rs: video.readyState,
    clicked: preStart.clicked,
  };
}
// Early report: a viewer who leaves in the first seconds may never reach the
// 30 s beat, and pagehide is not always delivered from an iframe.
for (const ms of [8_000, 20_000]) {
  setTimeout(() => {
    const s = memory.session;
    if (!s || firstFrameAtMs !== null) return;
    const body = beatBody();
    if (body) void fetch(`/v1/sessions/${s.sessionId}/beat`, { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {});
  }, ms);
}

function beatBody(ended = false): string | null {
  const s = memory.session;
  if (!s) return null;
  const body = {
    accessToken: s.accessToken,
    playing: !video.paused && !video.ended && video.readyState >= 2,
    position: Number.isFinite(video.currentTime) ? video.currentTime : 0,
    buffering: beatBuffering,
    errors: beatErrors,
    quality: video.videoHeight || undefined,
    firstFrameMs: firstFrameAtMs ?? undefined,
    ended,
    pre: preStartReport(),
    edgeErr: takeEdgeErrors(),
  };
  beatBuffering = 0;
  beatErrors = 0;
  return JSON.stringify(body);
}

setInterval(() => {
  const s = memory.session;
  const body = beatBody();
  if (!s || !body) return;
  void fetch(`/v1/sessions/${s.sessionId}/beat`, { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {});
}, 30_000);

window.addEventListener("pagehide", () => {
  const s = memory.session;
  const body = beatBody(true);
  if (!s || !body) return;
  try {
    navigator.sendBeacon(`/v1/sessions/${s.sessionId}/beat`, new Blob([body], { type: "application/json" }));
  } catch {
    /* best effort */
  }
});

// ─── Partner events (2026-10-28) ────────────────────────────────────────────
// The embedding site needs what its own player used to tell it: watch
// progress (continue-watching / "episodes watched"), the server that plays
// (its stream logs) and the end (auto-next). Public facts only, no tokens.
// The parent validates `event.source` + `event.origin` on its side.
function toParent(msg: Record<string, unknown>): void {
  if (window.parent === window) return;
  try {
    window.parent.postMessage({ ...msg, content: memory.content }, "*");
  } catch {
    /* best effort */
  }
}
let lastPlayServer = "";
video.addEventListener("playing", () => {
  const pb = memory.session?.playback;
  if (!pb) return;
  const label = pb.sources.find((s) => s.sourceId === pb.sourceId)?.label ?? "";
  if (label && label !== lastPlayServer) {
    lastPlayServer = label;
    toParent({ type: "whyworry:play", server: label.split(" · ")[0]?.trim() ?? label });
  }
});
let lastProgressAt = 0;
video.addEventListener("timeupdate", () => {
  const now = Date.now();
  if (now - lastProgressAt < 5000 || !Number.isFinite(video.duration) || video.duration <= 0) return;
  lastProgressAt = now;
  toParent({ type: "whyworry:progress", position: Math.floor(video.currentTime), duration: Math.floor(video.duration), playing: !video.paused });
});
video.addEventListener("ended", () => toParent({ type: "whyworry:ended" }));

// ?t=<seconds>: resume point from the partner (continue watching). Applied once.
let resumeApplied = false;
video.addEventListener("loadedmetadata", () => {
  if (resumeApplied) return;
  resumeApplied = true;
  const t = Number(new URLSearchParams(location.search).get("t") ?? 0);
  if (Number.isFinite(t) && t > 5 && Number.isFinite(video.duration) && t < video.duration - 30) video.currentTime = t;
});

/** YAMI sources whose first video piece reached THIS browser in the start race. */
const raceOk = new Set<string>();

/**
 * Start race (2026-11-04): the server can only check YAMI playlists (text);
 * whether the VIDEO pieces load is decided by the viewer's own browser (they
 * come straight from TikTok / anigato / megaplays). So the browser fetches the
 * first ~2 KB of the first segment of EVERY YAMI server at once and the first
 * one to answer becomes the active source. Zero cost (direct CDN bytes, no
 * Worker, no box video). Capped at RACE_MAX_MS; no winner ⇒ unchanged.
 */
async function raceYamiStart(): Promise<void> {
  const session = memory.session;
  if (!session) return;
  const pb = session.playback;
  const yami = pb.sources.filter((s) => !s.onDemand && /^YAMI\b/.test(s.label));
  if (yami.length === 0 || pb.format !== "hls") return;
  const RACE_MAX_MS = 3500;
  const t0 = performance.now();
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), RACE_MAX_MS);
  const gen = memory.generation;
  const manifestFor = (id: string) => pb.manifestUrl.replace(`/${encodeURIComponent(pb.sourceId)}/`, `/${encodeURIComponent(id)}/`).replace(`/${pb.sourceId}/`, `/${id}/`);
  const firstLine = (t: string) => t.split(/\r?\n/).map((l) => l.trim()).find((l) => l && !l.startsWith("#")) ?? null;
  const probe = async (id: string): Promise<string> => {
    const mUrl = new URL(manifestFor(id), location.href);
    const m = await fetch(mUrl, { signal: ctl.signal });
    const mt = await m.text();
    if (!m.ok || !mt.includes("#EXTM3U")) throw new Error("master");
    let list = mt;
    let base = mUrl;
    if (mt.includes("#EXT-X-STREAM-INF")) {
      const v = new URL(firstLine(mt)!, mUrl);
      const vr = await fetch(v, { signal: ctl.signal });
      list = await vr.text();
      if (!vr.ok || !list.includes("#EXTM3U")) throw new Error("variant");
      base = v;
    }
    const seg = firstLine(list);
    if (!seg) throw new Error("no segment");
    const s = await fetch(new URL(seg, base), { headers: { Range: "bytes=0-2047" }, signal: ctl.signal });
    if (!s.ok) throw new Error(`seg ${s.status}`);
    const b = await s.arrayBuffer();
    if (b.byteLength < 16) throw new Error("empty");
    raceOk.add(id);
    return id;
  };
  let winner: string | null = null;
  try {
    winner = await Promise.any(yami.map((s) => probe(s.sourceId)));
  } catch {
    winner = null; // every YAMI failed in this browser → normal order + 5 s rule
  }
  // Let slower YAMI finish being marked OK for later stall switches (no await).
  setTimeout(() => { clearTimeout(timer); ctl.abort(); }, 1500);
  if (!winner || gen !== memory.generation || memory.session !== session) return;
  console.log(`[whyworry] start race: ${pb.sources.find((s) => s.sourceId === winner)?.label} won in ${Math.round(performance.now() - t0)} ms`);
  if (winner === pb.sourceId) return;
  const { status, data } = await fetchJson<{ playback?: PublicPlayback }>(`/v1/sessions/${session.sessionId}/source`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sourceId: winner, accessToken: session.accessToken }),
  });
  if (status === 200 && data?.playback && memory.session === session) {
    session.playback = data.playback;
    refreshServerChrome();
  }
}

/** Adopt the session's current server list (keeps the active source). True when it grew. */
async function refreshSourceList(): Promise<boolean> {
  const session = memory.session;
  if (!session) return false;
  const { status, data } = await fetchJson<{ playback?: PublicPlayback }>(`/v1/sessions/${session.sessionId}/renew`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ accessToken: session.accessToken }),
  });
  if (status !== 200 || !data?.playback || memory.session !== session) return false;
  const before = session.playback.sources.length;
  session.playback = { ...session.playback, sources: data.playback.sources, mediaGrantExpiresAt: data.playback.mediaGrantExpiresAt };
  if (data.playback.sources.length > before) {
    refreshServerChrome();
    serverIndicator.textContent = data.playback.sources.length > 1 ? "AUTO FAILOVER" : "SOLO SOURCE";
    return true;
  }
  return false;
}

async function pollMoreSources(): Promise<void> {
  const gen = memory.generation;
  for (let i = 0; i < 10; i++) {
    await new Promise((r) => setTimeout(r, i < 5 ? 1500 : 3000));
    const session = memory.session;
    if (!session || gen !== memory.generation) return;
    const { status, data } = await fetchJson<{ playback?: PublicPlayback }>(
      `/v1/sessions/${session.sessionId}/renew`,
      { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ accessToken: session.accessToken }) }
    );
    if (status !== 200 || !data?.playback) return;
    const before = session.playback.sources.length;
    // Keep the ACTIVE source as-is; only adopt the (grown) list.
    session.playback = { ...session.playback, sources: data.playback.sources, mediaGrantExpiresAt: data.playback.mediaGrantExpiresAt };
    if (data.playback.sources.length > before) {
      refreshServerChrome();
      serverIndicator.textContent = data.playback.sources.length > 1 ? "AUTO FAILOVER" : "SOLO SOURCE";
      if (openMenuType === "servers") openMenu("servers");
    }
  }
}

// Dev-only debugging handle (stripped from production builds by Vite).
if (import.meta.env.DEV) {
  const w = window as unknown as Record<string, unknown>;
  w.__ww = { player: () => shakaPlayer };
  Object.defineProperty(w, "__wwShaka", { get: () => shakaPlayer });
  // Probe hook: switch server by label substring, exactly like a menu click.
  w.__wwSwitch = async (labelPart: string) => {
    const s = memory.session?.playback.sources.find((x) => x.label.includes(labelPart));
    if (!s) return "no such server";
    await handleSetting("server", s.sourceId);
    return `switched to ${s.label}`;
  };
  w.__wwSubs = () => subtitleTracks();
}

// Framed by a site (9ime, the playground): the player IS the page — no
// strip below it, no page scrollbar.
if (window.parent !== window) document.documentElement.classList.add("ww-embedded");
// ?strip=1: the host page wants the Server / Audio / Subtitles strip under the picture.
if (new URLSearchParams(location.search).get("strip") === "1") document.documentElement.classList.add("ww-strip");

void hydrate();
const bootSelector = parseEmbedPath() ?? selectorFromQuery();
void start(bootSelector);
void locked;
